import { RegistrationPasswordUpdatedDataSchema } from '@diamond/shared/schema/registration';
import { NoteValidationSchema } from '@diamond/shared/validation';
import { SavedMultipartFile } from '@fastify/multipart';
import { Static, TSchema, Type } from '@sinclair/typebox';

import { VOUCHER_CODE_MAX_LENGTH } from './constants';
import {
  DbSchema,
  DiscountPromo,
  FreeGoodPromo,
  GroupedPromo,
  Product,
} from './database';
import {
  Recipe,
  RecipeIngredient,
  RecipeIngredientGroup,
  RecipeMedia,
  RecipeProduct,
  RecipeTag,
} from './database.recipe';
import { Nullable, RecursiveStatic, TypeCustom } from './generics';
import {
  GroupPromoListBuyerSchema,
  OptionalWarehouseIdSchema,
  OrderDivisionFilterSchema,
  PaginationInfoSchema,
  PaginationSchema,
  ProductDivisionFilterSchema,
  ProductDivisionQuery,
  ReadManyOrderHistorySchema,
  ReadManyOrderTemplateItemSchema,
  ReadManyOrderTemplateSchema,
  ReadManyProductSchema,
  ReadManyRecipeSchema,
  ReadManySchema,
  ReadManyUserBuyerSchema,
  ReadWishlistSchema,
  SearchSchema,
  SortBySchema,
  SortFilterSchema,
  StorageFilterSchema,
  WarehouseIdCategorySchema,
  WarehouseIdSchema,
} from './read-many';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
export const ErrorSchema = Type.Object({
  error: Type.String(),
  message: Type.String(),
  statusCode: Type.Number(),
  data: Type.Optional(
    Type.Union([
      Type.Array(Type.Unknown()),
      Type.Record(Type.String(), Type.Unknown()),
    ])
  ),
});

export type ErrorResponse = Static<typeof ErrorSchema>;

export function addErrorSchemas(responseSchema: object): object {
  return {
    ...responseSchema,
    400: ErrorSchema,
    500: ErrorSchema,
  };
}

export const SAPErrorSchema = Type.Object({
  error: Type.Union([
    Type.Literal('SAP_PRICE_ERROR'),
    Type.Literal('SAP_STOCK_ERROR'),
    Type.Literal('SAP_SUBMIT_ORDER_ERROR'),
    Type.Literal('SAP_UNEXPECTED_ERROR'),
    Type.Literal('SAP_REQUEST_TIMEOUT_ERROR'),
    Type.Literal('SAP_CONNECTION_ERROR'),
    Type.Literal('SAP_SHIPMENT_DATES_ERROR'),
  ]),
  message: Type.String(),
  statusCode: Type.Literal(424),
  data: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
});

export function addSAPErrorSchemas(responseSchema: object): object {
  return {
    ...responseSchema,
    424: Type.Union([SAPErrorSchema, ErrorSchema]),
  };
}

// ---------------------------------------------------------------------------
// WebPortal
// ---------------------------------------------------------------------------
export const LoginWebPortalResponse = Type.Object({
  status: Type.Optional(Type.Number()),
  error: Type.Optional(Type.String()),
  access_token: Type.Optional(Type.String()),
  expire_in: Type.Optional(Type.Number()),
});
export type LoginWebPortal = Static<typeof LoginWebPortalResponse>;

export const UserRegisterWebPortalResponse = Type.Object({
  status: Type.Optional(Type.Any()),
  error: Type.Optional(Type.String()),
  data: Type.Optional(Type.Array(Type.String())),
  TicketNo: Type.Optional(Type.String()),
  code: Type.Optional(Type.Number()),
  msg: Type.Optional(Type.String()),
});
export type UserRegisterWebPortal = Static<
  typeof UserRegisterWebPortalResponse
>;

export const UserRegisterWebPortalRequest = Type.Object({
  FirstName: Type.String(),
  LastName: Type.String(),
  Email: Type.String(),
  Telp: Type.String(),
  DistChannel: Type.String(),
  CustomerID: Type.String(),
  CompanyName: Type.Optional(Type.String()),
});
export type UserRegisterWebPortalReq = Static<
  typeof UserRegisterWebPortalRequest
>;
// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------
export const ResponseSendEmailSchema = Type.Object({
  status: Type.Number(),
  id: Type.Optional(Type.String()),
  message: Type.String(),
});
export type ResponseSendEmail = Static<typeof ResponseSendEmailSchema>;

export const emailTemplateObjResponse = Type.Object({
  title: Type.Optional(Type.String()),
  subject: Type.Optional(Type.String()),
  header: Type.Optional(Type.String()),
  staticAssetsUrl: Type.Optional(Type.String()),
  homepageBaseUrl: Type.Optional(Type.String()),
  illustrationUrl: Type.Optional(Type.String()),
  resend: Type.Optional(Type.String()),
  url: Type.Optional(Type.String()),
  button_title: Type.Optional(Type.String()),
  billto_code: Type.Optional(Type.String()),
  billto_name: Type.Optional(Type.String()),
  user_email_before: Type.Optional(Type.String()),
  user_name_before: Type.Optional(Type.String()),
  user_email_after: Type.Optional(Type.String()),
  user_name_after: Type.Optional(Type.String()),
  requirement: Type.Optional(Type.String()),
  is_migration_user: Type.Optional(Type.Boolean()),
  is_reject_user: Type.Optional(Type.Boolean()),
  wa_number: Type.Optional(Type.String()),
  footer: Type.Optional(Type.String()),
  is_admin: Type.Optional(Type.Boolean()),
  should_not_render_button: Type.Optional(Type.Boolean()),
  customer_code: Type.Optional(Type.String()),
  customer_internal_url: Type.Optional(Type.String()),
  outlet: Type.Optional(Type.String()),
  voucher_count: Type.Optional(Type.Integer({ minimum: 0 })),
  voucher_guide_url: Type.Optional(Type.String()),
});

export type emailTemplateObj = Static<typeof emailTemplateObjResponse>;

// ---------------------------------------------------------------------------
// Masked Password for User
// ---------------------------------------------------------------------------
export const MaskedUserSchema = DbSchema['users'];

// ---------------------------------------------------------------------------
// ParamById Schema
// ---------------------------------------------------------------------------
export const paramByIDSchema = Type.Object({
  id: Type.String(),
});

export type ParamByID = Static<typeof paramByIDSchema>;

export const paramOrderItemSchema = Type.Intersect([
  paramByIDSchema,
  Type.Object({
    item_id: Type.String(),
  }),
]);

export type paramOrderItem = Static<typeof paramOrderItemSchema>;

export const paramConfirmEmailVerificationSchema = Type.Object({
  id: Type.String(),
  token: Type.String(),
});

export type paramConfirmEmailVerification = Static<
  typeof paramConfirmEmailVerificationSchema
>;

export const paramGetAccountAndContactSchema = Type.Intersect([
  paramByIDSchema,
  Type.Object({
    shipto_id: Type.String(),
  }),
]);

export type paramGetAccountAndContact = Static<
  typeof paramGetAccountAndContactSchema
>;

export const paramOrderProductHighlight = Type.Object({
  product_highlight_id: Type.String(),
  product_id: Type.String(),
});

export type ParamOrderProductHighlight = Static<
  typeof paramOrderProductHighlight
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function removeKey(obj: any) {
  delete obj['path'];
  for (const prop in obj) {
    delete obj[prop]['path'];
  }
  return obj;
}

// ---------------------------------------------------------------------------
// Promo
// ---------------------------------------------------------------------------
export const GenericFreeGoodPromoSchema = Type.Intersect([
  Type.Pick(GroupedPromo, ['id', 'mechanism']),
  Type.Pick(FreeGoodPromo, [
    'promo_code',
    'valid_from',
    'valid_until',
    'sequential_number_for_free_goods_scale',
    'free_goods_category',
    'quantity_free_goods',
  ]),
  Type.Pick(Product, ['title', 'sales_unit', 'sales_unit_desc']),
  Type.Object({
    product_id: Type.String({ format: 'uuid' }),
    product_item_code: Type.Integer(),
    product_images: Type.Array(Type.String()),
    product_image_thumbnails: Type.Array(Type.String()),
    product_images_medium: Type.Array(Type.String()),
    product_images_large: Type.Array(Type.String()),
    price: Type.Integer(),
  }),
]);

export const SingleFreeGoodPromoSchema = Type.Intersect([
  GenericFreeGoodPromoSchema,
  Type.Pick(FreeGoodPromo, ['minimum_order_quantity', 'minimum_order_amount']),
  Type.Object({
    ref_product_id: Type.String({ format: 'uuid' }), // reference-product's id -> product that make this promo eligible
    is_applies_in_multiples: Type.Boolean(),
  }),
]);

export const BundledFreeGoodPromoSchema = Type.Intersect([
  GenericFreeGoodPromoSchema,
  Type.Pick(FreeGoodPromo, [
    'sum_minimum_order_quantity',
    'sum_minimum_order_amount',
  ]),
]);

export const GenericDiscountPromoSchema = Type.Intersect([
  Type.Pick(GroupedPromo, ['id', 'mechanism']),
  Type.Pick(DiscountPromo, [
    'promo_code',
    'valid_from',
    'valid_until',
    'is_discount',
    'deduction_amount',
    'calculation_number',
  ]),
]);

export const SingleDiscountPromoSchema = Type.Intersect([
  GenericDiscountPromoSchema,
  Type.Pick(DiscountPromo, ['minimum_order_quantity', 'minimum_order_amount']),
  Type.Object({
    is_applies_in_multiples: Type.Boolean(),
    price_after_discount: Nullable(Type.Number()),
  }),
]);

export const BundledDiscountPromoSchema = Type.Intersect([
  GenericDiscountPromoSchema,
  Type.Pick(DiscountPromo, [
    'sum_minimum_order_quantity',
    'sum_minimum_order_amount',
  ]),
]);

export const CompiledProductDiscounts = Type.Array(
  Type.Object({
    product_id: Type.String({ format: 'uuid' }),
    is_discount: Type.Boolean(),
    deduction_amount: Type.Integer(),
    price_after_discount: Nullable(Type.Number()),
  })
);

// ---------------------------------------------------------------------------
// End of Promo
// ---------------------------------------------------------------------------

const ProductStockStatus = {
  AVAILABLE: 'AVAILABLE',
  LIMITED_STOCK: 'LIMITED_STOCK',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
} as const;
export type ProductStockStatus =
  (typeof ProductStockStatus)[keyof typeof ProductStockStatus];
export const ProductStockStatusSchema = Type.Enum(ProductStockStatus);

export const ProductResponseSchema = Type.Intersect([
  Type.Omit(DbSchema['products'], [
    'conversion_qty',
    'conversion_unit',
    'conversion_sales_unit',
    'category_id',
    'sub_category_id',
    'third_sub_category_id',
    'created_at',
    'updated_at',
    'deleted_at',
    'created_by',
    'updated_by',
    'deleted_by',
    'material_division',
    'can_overwrite_price',
    'category_2',
    'transportation_group',
    'flag_fs',
    'flag_gt',
    'flag_sts',
    'flag_show_pepperi',
    'is_valid_data',
    'finance_category_5',
    'finance_category_6',
    'finance_category_7',
    'suggestion_1',
    'suggestion_2',
    'tag',
    'is_only_for_sukanda_partner',
    'is_only_for_online_business',
    'is_eligible_for_so_automation',
  ]),
  Type.Object({
    stock: Type.Integer(),
    stock_status: ProductStockStatusSchema,
    category_name: Type.String(),
    wishlist_id: Nullable(Type.String({ format: 'uuid' })),
    sub_category_name: Type.Optional(Type.String()),
    sub_third_category_name: Type.Optional(Type.String()),
    product_images: Type.Array(Type.String()),
    product_image_thumbnails: Type.Array(Type.String()),
    product_images_medium: Type.Array(Type.String()),
    product_images_large: Type.Array(Type.String()),
    prices: Type.Array(
      Type.Object({
        price: Type.Number(),
        min_order_quantity: Type.Integer(),
      })
    ),
    stock_magnifier: Type.Integer(),
  }),
]);

export const ProductWithPromoResponseSchema = Type.Intersect([
  ProductResponseSchema,
  Type.Object({
    free_good_promos: Type.Array(SingleFreeGoodPromoSchema),
    bundled_free_good_promos: Type.Array(BundledFreeGoodPromoSchema),
    discount_promos: Type.Array(SingleDiscountPromoSchema),
    bundled_discount_promos: Type.Array(BundledDiscountPromoSchema),
  }),
]);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const AuthSchema = {
  path: '/auth',
  login: {
    path: '/login',
    body: Type.Object({
      email: Type.String({ format: 'email' }),
      password: Type.String({ minLength: 1 }),
      remember: Type.Optional(Type.Boolean()),
      subscriber: Type.Union([
        Type.Literal('web_buyer'),
        Type.Literal('web_admin'),
        Type.Literal('mobile'),
      ]),
      push_notification_token: Type.Optional(Type.String()),
    }),
    response: Type.Object({
      token: Type.String(),
      refresh_token: Type.String(),
    }),
  },
  forgotPassword: {
    path: '/forgot-password',
    body: Type.Object({
      email: Type.String({ format: 'email' }),
      subscriber: Type.Union([Type.Literal('admin'), Type.Literal('buyer')]),
    }),
    response: Type.Object({ message: Type.String() }),
  },
  resetPassword: {
    path: '/reset-password',
    body: Type.Object({
      token: Type.String(),
      old_password: Type.String({ minLength: 3 }),
      new_password: Type.String({ minLength: 3 }),
      confirm_new_password: Type.String({ minLength: 3 }),
    }),
    response: Type.Object({ message: Type.String() }),
  },
  verifyPassword: {
    path: '/reset-password/verify',
    body: Type.Object({
      email: Type.String({ format: 'email' }),
      new_password: Type.String({ minLength: 1 }),
      reset_password_token: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({ data: MaskedUserSchema }),
  },
  changePassword: {
    path: '/change-password',
    body: Type.Object({
      old_password: Type.String({ minLength: 3 }),
      new_password: Type.String({ minLength: 3 }),
      confirm_new_password: Type.String({ minLength: 3 }),
    }),
    response: Type.Object({ message: Type.String() }),
  },
  createPassword: {
    path: '/create-password',
    body: Type.Object({
      token: Type.String(),
      password: Type.String(),
      confirm_password: Type.String(),
    }),
    response: Type.Object({
      message: Type.String(),
      data: Type.Optional(RegistrationPasswordUpdatedDataSchema),
    }),
  },
  logout: {
    path: '/logout',
    response: Type.Null(),
  },
};
// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------
export const AddressSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  user_id: Type.String({ format: 'uuid' }),
  street_name: Type.String(),
  country: Type.String(),
  name_bill_to: Type.String(),
  address_bill_to: Type.String(),
  city_bill_to: Type.String(),
  sap_no_bilto: Type.Optional(Type.Number()),
});

export const AccountCrudSchema = {
  path: '/account',
  getMany: {
    path: '/',
    query: Type.Intersect([
      ReadManySchema,
      Type.Object({
        roles: Type.Optional(
          Type.Array(DbSchema['users']['properties']['role'])
        ),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(MaskedUserSchema),
      pagination: PaginationInfoSchema,
    }),
  },
  getById: {
    path: '/:id',
    response: Type.Intersect([
      MaskedUserSchema,
      Type.Object({
        addresses: Type.Array(AddressSchema),
        ship_tos: Type.Array(
          Type.Intersect([
            Type.Pick(DbSchema['user_ship_tos'], ['id']),
            Type.Pick(DbSchema['ship_tos'], [
              'name',
              'pic_name',
              'address',
              'sap_no_shipto',
              'cg_3_business_type',
              'cg_3_business_type_desc',
            ]),
            Type.Object({
              email: Type.Array(DbSchema['users']['properties']['email']),
              minimum_order: Type.Number(),
            }),
          ])
        ),
      }),
    ]),
  },
  getMe: {
    path: '/me',
    response: Type.Intersect([
      MaskedUserSchema,
      Type.Object({
        addresses: Type.Array(AddressSchema),
        ship_tos: Type.Array(
          Type.Intersect([
            Type.Pick(DbSchema['user_ship_tos'], ['id']),
            Type.Pick(DbSchema['ship_tos'], [
              'name',
              'pic_name',
              'address',
              'telp_shipto',
              'distribution_channel',
              'sap_no_shipto',
              'search_term_shipto',
              'cg_3_business_type',
              'cg_3_business_type_desc',
            ]),
            Type.Object({
              email: Type.Array(DbSchema['users']['properties']['email']),
              minimum_order: Type.Number(),
            }),
          ])
        ),
      }),
    ]),
  },
  getUserShipTo: {
    path: '/:id/ship-to',
    response: Type.Array(
      Type.Intersect([
        Type.Pick(DbSchema['user_ship_tos'], ['id']),
        Type.Pick(DbSchema['ship_tos'], [
          'name',
          'pic_name',
          'address',
          'distribution_channel',
        ]),
        Type.Object({
          email: Type.Array(DbSchema['users']['properties']['email']),
          minimum_order: Type.Number(),
        }),
      ])
    ),
  },
  deleteAccount: {
    path: '/delete-account',
    body: Type.Object({
      email: Type.String(),
      name: Type.String(),
      reason: Type.String(),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  register: {
    path: '/register',
    body: Type.Object({
      email: Type.String({ format: 'email' }),
      first_name: Type.String(),
      last_name: Type.String(),
      password: Type.String({ minLength: 1 }),
      confirm_password: Type.String({ minLength: 1 }),
      customer_code: Type.String(),
      company_name: Type.String(),
    }),
    response: Type.Object({
      message: Type.String(),
      no_ticket: Type.Optional(Type.String()),
    }),
  },
};
// ---------------------------------------------------------------------------
// Banner
// ---------------------------------------------------------------------------
export const BannerCrudSchema = {
  path: '/banner',
  createBanner: {
    path: '',
    body: Type.Omit(DbSchema['banner'], [
      'id',
      'created_at',
      'created_by',
      'updated_at',
      'updated_by',
      'deleted_at',
      'deleted_by',
      'filename',
    ]),
    response: DbSchema['banner'],
  },
  getManyBanner: {
    path: '',
    query: OptionalWarehouseIdSchema,
    response: Type.Array(
      Type.Intersect([
        Type.Omit(DbSchema['banner'], [
          'distribution_channel',
          'business_segmentation',
        ]),
        Type.Object({
          filename: Type.String(),
        }),
      ])
    ),
  },
  reorderBanner: {
    path: '/:id/reorder',
    body: Type.Pick(DbSchema['banner'], ['index']),
    response: Type.Array(DbSchema['banner']),
  },
  updateBanner: {
    path: '/:id',
    body: Type.Pick(DbSchema['banner'], [
      'title',
      'url',
      'image',
      'business_segmentation',
      'distribution_channel',
    ]),
    response: DbSchema['banner'],
  },
  deleteBanner: {
    path: '/:id',
    response: Type.Object({ message: Type.String() }),
  },
  uploadBanner: {
    path: '/upload',
    body: Type.Object({
      file: TypeCustom.Custom<SavedMultipartFile>(),
    }),
    response: Type.Object({
      url: Type.String(),
      filename: Type.String(),
    }),
  },
};
// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------
export const CartCrudSchema = {
  path: '/cart',
  getManyCart: {
    path: '',
    query: Type.Intersect([
      PaginationSchema,
      SortBySchema,
      Type.Pick(SearchSchema, ['search_query']),
      Type.Object({
        warehouse: Type.String({ format: 'uuid' }),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Omit(DbSchema['carts'], ['product_id', 'is_deleted']),
          Type.Object({
            product: ProductWithPromoResponseSchema,
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  createCart: {
    path: '',
    body: Type.Omit(DbSchema['carts'], ['id', 'user_id']),
    response: DbSchema['carts'],
  },
  updateCart: {
    path: '/:id',
    body: Type.Omit(DbSchema['carts'], [
      'id',
      'user_id',
      'product_id',
      'warehouse_id',
    ]),
    response: DbSchema['carts'],
  },
  deleteAllCart: {
    path: '/all',
    response: Type.Object({ message: Type.String() }),
  },
  deleteCart: {
    path: '/:id',
    response: Type.Object({ message: Type.String() }),
  },
  cartTotal: {
    path: '/total',
    body: Type.Object({
      warehouse_id: Type.String({ format: 'uuid' }),
      is_return_promos: Type.Boolean(),
      is_single_promo_only: Type.Optional(Type.Boolean({ default: false })),
      products: Type.Array(
        Type.Pick(DbSchema['carts'], ['product_id', 'quantity'])
      ),
      voucher_codes: Type.Optional(
        Type.Array(
          Type.String({
            minLength: 1,
            maxLength: VOUCHER_CODE_MAX_LENGTH,
            description:
              'Array of voucher codes to apply to the cart total calculation',
          })
        )
      ),
    }),
    response: Type.Object({
      original_total: Type.Integer(),
      final_total: Type.Integer(),
      saved_total: Type.Integer(),
      promos: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['orders'], ['division']),
          Type.Object({
            single_free_good_promos: Type.Array(SingleFreeGoodPromoSchema),
            single_discount_promos: Type.Array(
              Type.Intersect([
                SingleDiscountPromoSchema,
                Type.Object({ product_id: Type.String({ format: 'uuid' }) }),
              ])
            ),
            bundled_free_good_promos: Type.Array(BundledFreeGoodPromoSchema),
            bundled_discount_promos: Type.Array(BundledDiscountPromoSchema),
            compiled_product_discounts: CompiledProductDiscounts,
          }),
        ])
      ),
      vouchers: Type.Optional(
        Type.Object({
          applied_vouchers: Type.Array(
            Type.Object({
              voucher_id: Type.String({ format: 'uuid' }),
              voucher_code: Type.String({ maxLength: VOUCHER_CODE_MAX_LENGTH }),
              voucher_title: Type.String(),
              reward_type: Type.Union([
                Type.Literal('DISCOUNT_PERCENT'),
                Type.Literal('DISCOUNT_AMOUNT'),
                Type.Literal('FREE_GOODS'),
              ]),
              valid_from: Type.Optional(Type.String()),
              valid_until: Type.Optional(Type.String()),
              rule_products: Type.Optional(
                Type.Array(
                  Type.Object({
                    product_id: Nullable(Type.String({ format: 'uuid' })),
                    product_code: Type.String(),
                    product_item_code: Type.Integer({ minimum: 1 }),
                    min_quantity: Type.Optional(
                      Nullable(Type.Integer({ minimum: 0 }))
                    ),
                  }),
                  { default: [] }
                )
              ),
              free_goods: Type.Optional(
                Type.Array(
                  Type.Object({
                    product_id: Nullable(Type.String({ format: 'uuid' })),
                    product_code: Type.String(),
                    product_item_code: Type.Integer({ minimum: 1 }),
                    product_name: Type.String(),
                    product_brand_name: Type.String(),
                    quantity: Type.Integer({ minimum: 1 }),
                    division: Type.Union([
                      Type.Literal('diamond'),
                      Type.Literal('trading'),
                      Type.Literal('fnb'),
                      Type.Literal('equipment'),
                    ]),
                    product_images: Type.Array(Type.String(), { default: [] }),
                    product_images_medium: Type.Array(Type.String(), {
                      default: [],
                    }),
                    product_images_large: Type.Array(Type.String(), {
                      default: [],
                    }),
                    product_image_thumbnails: Type.Array(Type.String(), {
                      default: [],
                    }),
                    saved_amount: Type.Number({ minimum: 0 }),
                  })
                )
              ),
              product_discounts: Type.Optional(
                Type.Array(
                  Type.Object({
                    product_id: Nullable(Type.String({ format: 'uuid' })),
                    product_code: Type.String(),
                    product_item_code: Type.Integer({ minimum: 1 }),
                    original_price: Type.Number({ minimum: 0 }),
                    discount_percent: Type.Number({ minimum: 0, maximum: 100 }),
                    discount_amount: Type.Number({ minimum: 0 }),
                    final_price: Type.Number({ minimum: 0 }),
                    product_images: Type.Array(Type.String(), { default: [] }),
                    product_images_medium: Type.Array(Type.String(), {
                      default: [],
                    }),
                    product_images_large: Type.Array(Type.String(), {
                      default: [],
                    }),
                    product_image_thumbnails: Type.Array(Type.String(), {
                      default: [],
                    }),
                  })
                )
              ),
            })
          ),
          total_voucher_discount: Type.Number({ minimum: 0 }),
          invalid_vouchers: Type.Array(
            Type.Object({
              voucher_code: Type.String({ maxLength: VOUCHER_CODE_MAX_LENGTH }),
              error_message: Type.String(),
            })
          ),
        })
      ),
    }),
  },
};
// ---------------------------------------------------------------------------
// Order
// ---------------------------------------------------------------------------
export const OrderPaymentSchema = Type.Union([
  Type.Intersect([
    Type.Omit(DbSchema['order_payments'], ['order_meta_id']),
    Type.Object({
      proofs: Type.Array(
        Type.Pick(DbSchema['order_payment_proofs'], ['id', 'url', 'created_at'])
      ),
      total_diff: Type.Number(),
    }),
  ]),
  Type.Null(),
]);

export const OrderVoucherRewardDiscountSchema = Type.Pick(
  DbSchema['order_voucher_reward_discounts'],
  [
    'product_item_code',
    'product_name',
    'division',
    'quantity',
    'original_subtotal',
    'discount_amount',
    'discount_percent',
    'saved_amount',
    'final_subtotal',
  ]
);

export const OrderVoucherRewardFreeGoodSchema = Type.Intersect([
  Type.Pick(DbSchema['order_voucher_reward_free_goods'], [
    'product_item_code',
    'product_name',
    'division',
    'quantity',
    'saved_amount',
  ]),
  Type.Object({
    product_images: Type.Array(
      Type.Object({
        image: Type.String(),
        thumbnail_url: Nullable(Type.String()),
        medium_image_url: Nullable(Type.String()),
        large_image_url: Nullable(Type.String()),
      }),
      { default: [] }
    ),
  }),
]);

export const PopulatedOrderPromoSchema = Type.Omit(DbSchema['order_promos'], [
  'id',
  'order_id',
  'created_at',
  'updated_at',
]);

export const OrderVoucherAppliedSchema = Type.Object({
  order_voucher_id: Type.String({ format: 'uuid' }),
  voucher_id: Type.String({ format: 'uuid' }),
  order_id: Type.String({ format: 'uuid' }),
  voucher_code: Type.String({ maxLength: VOUCHER_CODE_MAX_LENGTH }),
  voucher_title: Type.String(),
  voucher_target_type: Type.Union([
    Type.Literal('GENERAL'),
    Type.Literal('SHIP_TO'),
  ]),
  period_start: Type.String(),
  period_end: Type.String(),
  reward_type: Type.Union([
    Type.Literal('DISCOUNT_PERCENT'),
    Type.Literal('DISCOUNT_AMOUNT'),
    Type.Literal('FREE_GOODS'),
  ]),
  total_discount_amount: Type.Number({ minimum: 0 }),
  min_purchase_amount: Nullable(Type.Number({ minimum: 0 })),
  min_total_quantity_all_item: Nullable(Type.Integer({ minimum: 0 })),
  is_bundling: Type.Boolean(),
  allow_other_vouchers: Type.Boolean(),
  allow_other_promos: Type.Boolean(),
  allow_special_prices: Type.Boolean(),
  product_discounts: Type.Array(OrderVoucherRewardDiscountSchema),
  free_goods: Type.Array(OrderVoucherRewardFreeGoodSchema),
});

const OrderItemOmitBaseFields = [
  'id',
  'order_id',
  'product_id',
  'line_item',
  'line_item_sap',
  'original_price',
  'original_stock',
] as const;

const OrderItemFulfillmentQtySubtotalOmitFields = [
  'processed_quantity',
  'delivered_quantity',
  'received_quantity',
  'processed_subtotal',
  'delivered_subtotal',
  'received_subtotal',
] as const;

const buildOrderItemSchema = (
  orderItemOmitFields: readonly (
    | (typeof OrderItemOmitBaseFields)[number]
    | (typeof OrderItemFulfillmentQtySubtotalOmitFields)[number]
  )[]
) =>
  Type.Array(
    Type.Intersect([
      Type.Omit(DbSchema['orders'], [
        'order_meta_id',
        'so_number',
        'is_submitted',
        'po_effective_date',
      ]),
      Type.Object({
        reason: Type.Optional(Nullable(Type.String())),
        cancel_reason: Type.Optional(Nullable(Type.String())),
        cancel_reason_id: Type.Optional(Nullable(Type.String())),
        products: Type.Array(
          Type.Intersect([
            Type.Omit(ProductResponseSchema, [
              'wishlist_id',
              'sub_category_name',
              'sub_third_category_name',
              'prices',
              'stock_status',
            ]),
            Type.Omit(DbSchema['order_items'], orderItemOmitFields),
            Type.Object({
              stock_magnifier: Type.Integer(),
              subtotal_diff: Type.Number(),
              voucher_discounts: Type.Array(OrderVoucherRewardDiscountSchema),
              voucher_free_goods: Type.Array(OrderVoucherRewardFreeGoodSchema),
            }),
          ])
        ),
        promos: PopulatedOrderPromoSchema,
      }),
    ])
  );

export const OrderItemSchema = buildOrderItemSchema([
  ...OrderItemOmitBaseFields,
  ...OrderItemFulfillmentQtySubtotalOmitFields,
]);

export const GetOneOrderItemSchema = buildOrderItemSchema([
  ...OrderItemOmitBaseFields,
]);

export const OrderVoucherSummarySchema = Type.Object({
  applied_vouchers: Type.Array(OrderVoucherAppliedSchema),
  total_voucher_discount: Type.Number({ minimum: 0 }),
  reward_by_item_code: Type.Record(
    Type.String(),
    Type.Object({
      discounts: Type.Array(OrderVoucherRewardDiscountSchema),
      free_goods: Type.Array(OrderVoucherRewardFreeGoodSchema),
    })
  ),
});

export const OrderCrudSchema = {
  path: '/order',

  // ------------------------------------------------------
  // Order ( / )
  // ------------------------------------------------------
  createOrder: {
    path: '',
    body: Type.Object({
      warehouse_id: Type.String({ format: 'uuid' }),
      products: Type.Array(
        Type.Object({
          id: Type.String({ format: 'uuid' }),
          quantity: Type.Integer({ exclusiveMinimum: 0 }),
          note: Type.Optional(NoteValidationSchema),
        }),
        { minItems: 1 }
      ),
      voucher_codes: Type.Optional(
        Type.Array(
          Type.String({
            minLength: 1,
            maxLength: VOUCHER_CODE_MAX_LENGTH,
          })
        )
      ),
    }),
    response: Type.Omit(DbSchema['order_metas'], ['is_dummy']),
  },
  // createSubmittedOrder: {
  //   path: '/meta/:id/submit',
  //   params: paramByIDSchema,
  //   response: Type.Intersect([
  //     Type.Omit(DbSchema['order_metas'], ['warehouse_id']),
  //     Type.Object({
  //       warehouse: DbSchema['ship_tos'],
  //       payment: Type.Union([
  //         Type.Omit(DbSchema['order_payments'], ['order_meta_id']),
  //         Type.Object({
  //           proofs: Type.Array(
  //             Type.Omit(DbSchema['order_payment_proofs'], ['order_payment_id'])
  //           ),
  //         }),
  //         Type.Null(),
  //       ]),
  //       items: Type.Array(
  //         Type.Intersect([
  //           Type.Omit(DbSchema['orders'], ['order_meta_id']),
  //           Type.Object({
  //             products: Type.Array(
  //               Type.Intersect([
  //                 Type.Omit(ProductResponseSchema, [
  //                   'wishlist_id',
  //                   'warehouse_id',
  //                   'sub_category_name',
  //                   'prices',
  //                   'stock_status',
  //                 ]),
  //                 Type.Omit(DbSchema['order_items'], [
  //                   'id',
  //                   'order_id',
  //                   'product_id',
  //                 ]),
  //               ])
  //             ),
  //           }),
  //         ])
  //       ),
  //     }),
  //   ]),
  // },
  getOneOrder: {
    path: '/meta/:id',
    query: WarehouseIdSchema,
    params: paramByIDSchema,
    response: Type.Intersect([
      Type.Omit(DbSchema['order_metas'], ['purchase_order_docs', 'is_dummy']),
      Type.Object({
        po_effective_date: Type.Number({ minimum: 0 }),
        stock_magnifier: Type.Integer(),
        transaction_date: Type.Number({ minimum: 0 }),
        payment: OrderPaymentSchema,
        items: GetOneOrderItemSchema,
        vouchers: OrderVoucherSummarySchema,
        total_voucher_discount: Type.Number({ minimum: 0 }),
        bank_account: Type.Union([
          Type.Intersect([
            Type.Omit(DbSchema['bank_accounts'], ['id']),
            Type.Object({
              bank_account_holder: Type.String(),
            }),
          ]),
          Type.Null(),
        ]),
        purchase_order_docs: Type.Array(
          Type.Pick(DbSchema['order_po_docs'], ['order_meta_id', 'url', 'id'])
        ),
        original_total: Type.Integer(),
        rejected_items_count: Type.Integer({ minimum: 0 }),
        rejected_items_string: Type.Union([Type.String(), Type.Null()]),
        updated_items_count: Type.Integer({ minimum: 0 }),
        updated_items_string: Type.Union([Type.String(), Type.Null()]),
      }),
    ]),
  },
  getDeliveryOrdersByMetaId: {
    path: '/meta/:id/delivery-orders',
    query: WarehouseIdSchema,
    params: paramByIDSchema,
    response: Type.Object({
      success: Type.Boolean(),
      data: Type.Object({
        order_meta_id: Type.String({ format: 'uuid' }),
        total_orders: Type.Integer({ minimum: 0 }),
        total_delivery_orders: Type.Integer({ minimum: 0 }),
        orders: Type.Array(
          Type.Object({
            order_id: Type.String({ format: 'uuid' }),
            order_number: Type.String(),
            sub_po_number: Type.String(),
            division: Type.Union([
              Type.Literal('diamond'),
              Type.Literal('trading'),
              Type.Literal('fnb'),
              Type.Literal('equipment'),
              Type.Literal('food'),
            ]),
            pending_items: Type.Array(
              Type.Intersect([
                Type.Object({
                  order_item_id: Type.String({ format: 'uuid' }),
                  item_code: Type.Integer(),
                  product_name: Type.String(),
                  sales_unit: Type.String(),
                  ordered_quantity: Type.Integer({ minimum: 0 }),
                  delivered_quantity: Type.Integer({ minimum: 0 }),
                  pending_quantity: Type.Integer({ minimum: 0 }),
                  created_at: Nullable(Type.Integer({ minimum: 0 })),
                  updated_at: Nullable(Type.Integer({ minimum: 0 })),
                }),
                Type.Pick(DbSchema['products'], ['product_storage']),
              ])
            ),
            delivery_orders: Type.Array(
              Type.Object({
                id: Type.String({ format: 'uuid' }),
                do_number: Nullable(Type.String()),
                status: Type.Union([
                  Type.Literal('processed'),
                  Type.Literal('delivered'),
                  Type.Literal('received'),
                  Type.Literal('rejected'),
                ]),
                do_processed_at: Nullable(Type.String({ format: 'date' })),
                do_delivered_at: Nullable(Type.String({ format: 'date' })),
                do_received_at: Nullable(Type.String({ format: 'date' })),
                do_rejected_at: Nullable(Type.String({ format: 'date' })),
                cancel_reason: Nullable(Type.String()),
                created_at: Type.Integer({ minimum: 0 }),
                updated_at: Type.Integer({ minimum: 0 }),
                items: Type.Array(
                  Type.Intersect([
                    Type.Object({
                      id: Type.String({ format: 'uuid' }),
                      order_item_id: Type.String({ format: 'uuid' }),
                      item_code: Type.Integer(),
                      quantity: Type.Integer({ minimum: 0 }),
                      ordered_quantity: Type.Integer({ minimum: 0 }),
                      cancel_reason: Nullable(Type.String()),
                      product_name: Type.String(),
                      sales_unit: Type.String(),
                      created_at: Type.Integer({ minimum: 0 }),
                      updated_at: Type.Integer({ minimum: 0 }),
                    }),
                    Type.Pick(DbSchema['products'], ['product_storage']),
                  ])
                ),
              })
            ),
          })
        ),
      }),
    }),
  },
  updateOrder: {
    path: '/meta/:id',
    params: paramByIDSchema,
    body: Type.Intersect([
      Type.Pick(DbSchema['order_metas'], ['payment_method']),
      Type.Object({
        delivery_note: Type.Optional(NoteValidationSchema),
      }),
      Type.Object({
        purchase_order_docs: Type.Optional(
          Type.Array(Type.String({ format: 'uri', minLength: 1 }))
        ),
      }),
      Type.Object({
        ship_to_contact: Type.Object({
          name: Type.String({ minLength: 1 }),
          telp: Type.String({ minLength: 1 }),
        }),
      }),
      Type.Partial(
        Type.Pick(DbSchema['order_metas'], ['delivery_option', 'delivery_date'])
      ),
    ]),
    response: Type.Intersect([
      Type.Omit(DbSchema['order_metas'], [
        'purchase_order_docs',
        'is_dummy',
        'original_total', // temporary omitting, not touching backoffice yet
        'saved_total', // temporary omitting, not touching backoffice yet
      ]),
      Type.Object({
        purchase_order_docs: Type.Array(
          Type.Pick(DbSchema['order_po_docs'], ['order_meta_id', 'url', 'id'])
        ),
      }),
    ]),
  },
  uploadPurchaseOrderDocument: {
    path: '/purchase-order-document/upload',
    body: Type.Object({
      files: TypeCustom.Custom<SavedMultipartFile[]>(),
    }),
    response: Type.Object({
      urls: Type.Array(Type.String()),
    }),
  },
  updatePaymentProof: {
    path: '/payment-proof/:id',
    params: paramByIDSchema,
    body: Type.Intersect([
      Type.Pick(DbSchema['order_payments'], ['bank_name', 'bank_account']),
      Type.Object({
        url: Type.Array(Type.String({ format: 'uri' })),
      }),
    ]),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  historyOrders: {
    path: '/histories',
    query: ReadManyOrderHistorySchema,
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['orders'], [
            'id',
            'order_meta_id',
            'original_subtotal',
            'subtotal',
            'division',
            'sub_po_number',
            'so_number',
            'delivery_link',
            'invoice_link',
            'sap_note',
            'po_effective_date',
          ]),
          Type.Object({
            warehouse_id: Type.String({ format: 'uuid' }),
            transaction_date: Type.Number({ minimum: 0 }),
            expired_at: Type.Optional(Type.Integer({ minimum: 0 })),
            transaction_id: Type.String(),
            status: Type.Union([
              Type.Literal('draft'),
              Type.Literal('submitted'),
              Type.Literal('waiting_for_payment'),
              Type.Literal('processed'),
              Type.Literal('out_for_delivery'),
              Type.Literal('received'),
              Type.Literal('partially_received'),
              Type.Literal('cancelled'),
            ]),
            items: Type.Array(
              Type.Intersect([
                Type.Pick(ProductResponseSchema, [
                  'title',
                  'division',
                  'desc',
                  'brand_name',
                  'brand_desc',
                  'item_code',
                ]),
                Type.Object({
                  images: Type.Array(Type.String()),
                  image_thumbnails: Type.Array(Type.String()),
                  images_medium: Type.Array(Type.String()),
                  images_large: Type.Array(Type.String()),
                }),
                Type.Pick(DbSchema['order_items'], ['quantity']),
              ])
            ),
            stock_magnifier: Type.Integer(),
            rejected_items_count: Type.Integer({ minimum: 0 }),
            rejected_items_string: Type.Union([Type.String(), Type.Null()]),
            updated_items_count: Type.Integer({ minimum: 0 }),
            updated_items_string: Type.Union([Type.String(), Type.Null()]),
            reason: Nullable(Type.String()),
            cancel_reason: Nullable(Type.String()),
            cancel_reason_id: Nullable(Type.String()),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  getOrderFilter: {
    path: '/filters',
    query: Type.Object({ distribution_channel: Type.String() }),
    response: Type.Object({
      divisions: OrderDivisionFilterSchema,
      storages: StorageFilterSchema,
      sorts: SortFilterSchema,
    }),
  },
  shipmentDates: {
    path: '/shipment-dates',
    query: WarehouseIdSchema,
    response: Type.Object({
      dates_available: Type.Array(Type.String()),
      dates_available_ui: Type.Array(Type.String()),
    }),
  },
  getOrderValidation: {
    path: '/meta/:id/order-validation',
    params: paramByIDSchema,
    response: Type.Object({
      code: Type.Union([
        Type.Literal('DIFF_PRICE_OR_STOCK'),
        Type.Literal('ALL_ORDER_READY_TO_PROCESS'),
        Type.Literal('PARTIALLY_OUT_OF_STOCK'),
        Type.Literal('INVALID_PROMOS_PRESENT'),
      ]),
      message: Type.String(),
      invalid_promos: Type.Pick(DbSchema['order_promos'], [
        'single_free_good_promos',
        'single_discount_promos',
        'bundled_free_good_promos',
        'bundled_discount_promos',
      ]),
      voucher_validation: Type.Optional(
        Type.Object({
          invalid_vouchers: Type.Array(
            Type.Object({
              voucher_id: Type.String(),
              voucher_code: Type.String(),
              reason: Type.Union([
                Type.Literal('period_inactive'),
                Type.Literal('quota_exhausted'),
                Type.Literal('per_shipto_limit_exceeded'),
                Type.Literal('min_purchase_not_met'),
                Type.Literal('bundling_quantity_not_met'),
                Type.Literal('rule_products_not_met'),
                Type.Literal('free_goods_insufficient_stock'),
              ]),
            })
          ),
          free_goods_stock_validation: Type.Optional(
            Type.Object({
              has_insufficient_stock: Type.Boolean(),
              insufficient_stock_items: Type.Array(
                Type.Object({
                  product_item_code: Type.Integer(),
                  required_quantity: Type.Integer(),
                  available_stock: Type.Integer(),
                  cart_quantity: Type.Integer(),
                  free_goods_quantity: Type.Integer(),
                })
              ),
            })
          ),
        })
      ),
      data: Type.Array(
        Type.Intersect([
          Type.Pick(ProductResponseSchema, [
            'id',
            'item_code',
            'title',
            'brand_name',
            'brand_desc',
          ]),
          Type.Pick(DbSchema['order_items'], ['price']),
          Type.Object({
            product_image: Type.String(),
            price_diff: Type.Integer(),
            stock: Type.Integer(),
            stock_diff: Type.Integer(),
            is_price_diff: Type.Boolean(),
            is_stock_diff: Type.Boolean(),
            stock_magnifier: Type.Integer(),
          }),
        ])
      ),
    }),
    forbidden_response: Type.Object({
      message: Type.String(),
      error: Type.Union([
        Type.Literal('ALL_ITEM_CANT_BE_PROCESSED'),
        Type.Literal('MIN_ORDER_NOT_REACHED'),
        Type.Literal('MIN_ORDER_NOT_REACHED_DUE_TO_TOTAL_DIFF'),
      ]),
      statusCode: Type.Number(),
      data: Type.Array(
        Type.Intersect([
          Type.Pick(ProductResponseSchema, [
            'id',
            'item_code',
            'title',
            'brand_name',
            'brand_desc',
          ]),
          Type.Pick(DbSchema['order_items'], ['price']),
          Type.Object({
            product_image: Type.String(),
            price_diff: Type.Integer(),
            stock: Type.Integer(),
            stock_diff: Type.Integer(),
            is_price_diff: Type.Boolean(),
            is_stock_diff: Type.Boolean(),
            stock_magnifier: Type.Integer(),
          }),
        ])
      ),
    }),
  },

  // ------------------------------------------------------
  // Backoffice Order ( /backoffice/ )
  // ------------------------------------------------------
  readManyOrderBackoffice: {
    path: '',
    query: ReadManySchema,
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          DbSchema['orders'],
          Type.Object({
            user: Type.Omit(DbSchema['users'], [
              'created_at',
              'updated_at',
              'deleted_at',
              'created_by',
              'updated_by',
              'deleted_by',
            ]),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
      total: Type.Number(),
      total_items: Type.Number(),
    }),
  },

  // ------------------------------------------------------
  // Payment Proof ( /:order_id/payment/:id/proof )
  // ------------------------------------------------------
  uploadPaymentProof: {
    path: '/payment-proof/upload',
    body: Type.Object({
      files: TypeCustom.Custom<SavedMultipartFile[]>(),
    }),
    response: Type.Object({
      urls: Type.Array(Type.String()),
    }),
  },
  addPaymentProof: {
    path: '/meta/:order_meta_id/payment/:order_payment_id/proof',
    body: Type.Object({
      url: Type.String({ format: 'uri' }),
    }),
    params: Type.Object({
      order_meta_id: Type.String({ format: 'uuid' }),
      order_payment_id: Type.String({ format: 'uuid' }),
    }),
    response: DbSchema['order_payment_proofs'],
  },
  deletePaymentProof: {
    path: '/meta/:order_meta_id/payment/:order_payment_id/proof/:id',
    params: Type.Intersect([
      paramByIDSchema,
      Type.Object({
        order_meta_id: Type.String({ format: 'uuid' }),
        order_payment_id: Type.String({ format: 'uuid' }),
      }),
    ]),
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // Order Template ( /template )
  // ------------------------------------------------------
  createOrderTemplate: {
    path: '/template',
    body: Type.Intersect([
      Type.Omit(DbSchema['order_templates'], [
        'id',
        'user_id',
        'created_by',
        'note',
      ]),
      Type.Object({
        note: Type.Optional(Type.String()),
        warehouse_id: Type.String({ format: 'uuid' }),
      }),
    ]),
    response: DbSchema['order_templates'],
  },
  getManyOrderTemplate: {
    path: '/template',
    query: Type.Intersect([ReadManyOrderTemplateSchema]),
    response: Type.Object({
      data: Type.Array(DbSchema['order_templates']),
      pagination: PaginationInfoSchema,
    }),
  },
  getOneOrderTemplate: {
    path: '/template/:id',
    params: paramByIDSchema,
    response: DbSchema['order_templates'],
  },
  updateOrderTemplate: {
    path: '/template/:id',
    params: paramByIDSchema,
    body: Type.Intersect([
      Type.Omit(DbSchema['order_templates'], [
        'id',
        'user_id',
        'created_by',
        'note',
      ]),
      Type.Object({
        note: Type.Optional(Type.String()),
        warehouse_id: Type.String(),
      }),
    ]),
    response: DbSchema['order_templates'],
  },
  deleteOrderTemplate: {
    path: '/template/:id',
    params: paramByIDSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },
  addToCartFromOrderTemplate: {
    path: '/template/:id/add-to-cart',
    params: paramByIDSchema,
    body: WarehouseIdSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // Order Template Item ( /:id/item )
  // -----------------------------------------------------
  addOrderTemplateItem: {
    path: '/template/:id/item',
    body: Type.Object({
      items: Type.Array(
        Type.Omit(DbSchema['order_template_items'], [
          'id',
          'order_template_id',
          'created_at',
          'updated_at',
        ])
      ),
    }),
    params: paramByIDSchema,
    response: Type.Object({
      items: Type.Array(DbSchema['order_template_items']),
    }),
  },
  getManyOrderTemplateItem: {
    path: '/template/:id/item',
    params: paramByIDSchema,
    query: Type.Intersect([ReadManyOrderTemplateItemSchema, WarehouseIdSchema]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['order_template_items'], ['id', 'quantity']),
          Type.Object({
            product: ProductWithPromoResponseSchema,
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  deleteProductFromOrderTemplate: {
    path: '/template/:id/item',
    params: paramByIDSchema,
    body: {
      product_id: Type.String(),
    },
    response: Type.Object({
      message: Type.String(),
    }),
  },
  deleteAllProductFromOrderTemplate: {
    path: '/template/:id/item/all',
    params: paramByIDSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },
  updateOrderTemplateItem: {
    path: '/template/:id/item',
    body: Type.Object({
      items: Type.Array(
        Type.Omit(DbSchema['order_template_items'], [
          'id',
          'order_template_id',
          'created_at',
          'updated_at',
        ])
      ),
    }),
    params: paramByIDSchema,
    response: Type.Object({
      items: Type.Array(DbSchema['order_template_items']),
    }),
  },

  // ------------------------------------------------------
  // Minimum Spend ( /minimum-spend )
  // ------------------------------------------------------
  readManyMinimumSpend: {
    path: '/minimum-spend',
    response: Type.Object({
      key: Nullable(Type.String()),
      url: Nullable(Type.String()),
      data: Type.Array(Type.Omit(DbSchema['minimum_spends'], ['url', 'key'])),
    }),
  },
  readOneMinimumSpend: {
    path: '/minimum-spend',
    query: Type.Object({
      warehouse: Type.String({
        format: 'uuid',
      }),
    }),
    response: DbSchema['minimum_spends'],
  },
  uploadMinimumSpend: {
    path: '/minimum-spend/upload',
    body: Type.Object({
      file: TypeCustom.Custom<SavedMultipartFile>(),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  deleteMinimumSpend: {
    path: '/minimum-spend',
    body: Type.Object({
      key: Type.String(),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },
  downloadTemplateMinimumSpend: {
    path: '/minimum-spend/download/template',
  },
};
// ---------------------------------------------------------------------------
// Product
// ---------------------------------------------------------------------------
export const ProductCrudSchema = {
  path: '/product',
  getMany: {
    path: '',
    query: ReadManyProductSchema,
    response: Type.Object({
      data: Type.Array(ProductWithPromoResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },
  getProductRecommendations: {
    path: '/recommendations',
    query: Type.Object({
      search_query: Type.String(),
      warehouse: Type.String(),
    }),
    response: Type.Object({
      products: Type.Array(Type.String()),
      brands: Type.Array(Type.String()),
    }),
  },
  getProductSubstitutions: {
    path: '/:id/substitutions',
    params: paramByIDSchema,
    query: Type.Omit(ReadManyProductSchema, ['search_query']),
    response: Type.Object({
      data: Type.Array(ProductWithPromoResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },
  getById: {
    path: '/:id',
    query: WarehouseIdSchema,
    response: Type.Intersect([
      ProductWithPromoResponseSchema,
      Type.Object({
        count_recipes: Type.Integer({ minimum: 0 }),
      }),
    ]),
  },
  getManyCategory: {
    path: '/category',
    query: Type.Intersect([
      PaginationSchema,
      Type.Pick(SearchSchema, ['search_query']),
      ProductDivisionQuery,
      WarehouseIdCategorySchema,
      Type.Object({
        storage_code: Type.Optional(
          Type.Union([
            Type.Literal('dry'),
            Type.Literal('frozen'),
            Type.Literal('dry_ac'),
            Type.Literal('chilled'),
          ])
        ),
        product_id_substitute: Type.Optional(Type.String({ format: 'uuid' })),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Omit(DbSchema['product_categories'], [
            'updated_at',
            'deleted_at',
            'created_by',
            'updated_by',
            'deleted_by',
          ]),
          Type.Object({
            product_count: Type.Number(),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  getSubCategoryById: {
    path: '/sub-category/:id',
    query: Type.Intersect([
      Type.Pick(SearchSchema, ['search_query']),
      ProductDivisionQuery,
      WarehouseIdCategorySchema,
      Type.Object({
        storage_code: Type.Optional(
          Type.Union([
            Type.Literal('dry'),
            Type.Literal('frozen'),
            Type.Literal('dry_ac'),
            Type.Literal('chilled'),
          ])
        ),
        product_id_substitute: Type.Optional(Type.String({ format: 'uuid' })),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Object({
          category_lv2: Type.String({ format: 'uuid' }),
          name: Type.String(),
          product_count: Type.Number(),
          third_category: Type.Array(
            Type.Object({
              category_lv3: Type.String({ format: 'uuid' }),
              name: Type.String(),
              product_count: Type.Number(),
            })
          ),
        })
      ),
    }),
  },
  getProductFilter: {
    path: '/filters',
    response: Type.Object({
      divisions: ProductDivisionFilterSchema,
      storages: StorageFilterSchema,
      sorts: SortFilterSchema,
    }),
  },

  // ------------------------------------------------------
  // User Highlight ( /product/highlight )
  // ------------------------------------------------------
  readHighlight: {
    path: '/highlight',
    query: WarehouseIdSchema,
    response: Type.Array(
      Type.Intersect([
        Type.Omit(DbSchema['product_highlights'], [
          'created_at',
          'updated_at',
          'deleted_at',
          'created_by',
          'updated_by',
          'deleted_by',
        ]),
        Type.Object({
          products: Type.Array(ProductWithPromoResponseSchema),
        }),
      ])
    ),
  },
  readHighlights: {
    path: '/highlights',
    query: WarehouseIdSchema,
    response: Type.Array(
      Type.Omit(DbSchema['product_highlights'], [
        'created_at',
        'updated_at',
        'deleted_at',
        'created_by',
        'updated_by',
        'deleted_by',
      ])
    ),
  },
  getManyProductFromHighlight: {
    path: '/highlight/:id/item',
    params: paramByIDSchema,
    query: Type.Intersect([PaginationSchema, WarehouseIdSchema]),
    response: Type.Object({
      data: Type.Array(ProductWithPromoResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },

  // ------------------------------------------------------
  // Admin Highlight ( backoffice/product/highlight )
  // ------------------------------------------------------
  createHighlight: {
    path: '/highlight',
    body: Type.Intersect([
      Type.Omit(DbSchema['product_highlights'], [
        'id',
        'created_at',
        'updated_at',
        'deleted_at',
        'created_by',
        'updated_by',
        'deleted_by',
      ]),
      Type.Object({
        products: Type.Optional(
          Type.Array(Type.String({ format: 'uuid' }), { minItems: 1 })
        ),
      }),
    ]),
    response: DbSchema['product_highlights'],
  },
  getManyHighlight: {
    path: '/highlight',
    response: Type.Array(
      Type.Intersect([
        Type.Omit(DbSchema['product_highlights'], [
          'created_at',
          'updated_at',
          'deleted_at',
          'created_by',
          'updated_by',
          'deleted_by',
        ]),
        Type.Object({
          images: Type.Array(Type.String({ format: 'uri' })),
          image_thumbnails: Type.Array(Type.String()),
        }),
      ])
    ),
  },
  getOneHighlight: {
    path: '/highlight/:id',
    query: WarehouseIdSchema,
    params: paramByIDSchema,
    response: DbSchema['product_highlights'],
  },
  reorderHighlight: {
    path: `/highlight/:id/reorder`,
    params: paramByIDSchema,
    body: Type.Pick(DbSchema['product_highlights'], ['index']),
    response: Type.Array(DbSchema['product_highlights']),
  },
  updateHighlight: {
    path: '/highlight/:id',
    params: paramByIDSchema,
    body: Type.Pick(DbSchema['product_highlights'], [
      'title',
      'business_segmentation',
      'distribution_channel',
    ]),
    response: DbSchema['product_highlights'],
  },
  deleteHighlight: {
    path: '/highlight/:id',
    params: paramByIDSchema,
    response: Type.Object({ message: Type.String() }),
  },
  addProductToHighlight: {
    path: '/highlight/:id/item',
    params: paramByIDSchema,
    body: Type.Object({
      products: Type.Array(Type.String({ format: 'uuid' }), { minItems: 1 }),
    }),
    response: Type.Object({ message: Type.String() }),
  },
  deleteProductFromHighlight: {
    path: '/highlight/:id/item',
    params: paramByIDSchema,
    body: Type.Object({
      products: Type.Array(Type.String({ format: 'uuid' })),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // Keyword ( /product/keyword )
  // ------------------------------------------------------
  addProductKeyword: {
    path: '/keyword',
    body: Type.Pick(DbSchema['product_keywords'], [
      'name',
      'url',
      'index',
      'business_segmentation',
      'distribution_channel',
    ]),
    response: DbSchema['product_keywords'],
  },
  reorderProductKeyword: {
    path: '/keyword/:id/reorder',
    body: Type.Pick(DbSchema['product_keywords'], ['index']),
    response: Type.Array(DbSchema['product_keywords']),
  },
  getManyProductKeyword: {
    path: '/keyword',
    query: WarehouseIdSchema,
    response: Type.Array(DbSchema['product_keywords']),
  },
  getProductKeywordById: {
    path: '/keyword/:id',
    response: DbSchema['product_keywords'],
  },
  updateProductKeyword: {
    path: '/keyword/:id',
    body: Type.Pick(DbSchema['product_keywords'], [
      'name',
      'url',
      'business_segmentation',
      'distribution_channel',
    ]),
    response: DbSchema['product_keywords'],
  },
  deleteProductKeyword: {
    path: '/keyword/:id',
    response: Type.Object({
      message: Type.String(),
    }),
  },
};
// ---------------------------------------------------------------------------
// Wishlist
// ---------------------------------------------------------------------------
export const WishlistCrudSchema = {
  path: '/wishlist',
  createWishList: {
    path: '',
    body: Type.Omit(DbSchema['wishlist'], [
      'id',
      'user_id',
      'created_at',
      'updated_at',
      'deleted_at',
    ]),
    response: DbSchema['wishlist'],
  },
  readManyWishList: {
    path: '',
    query: Type.Intersect([ReadWishlistSchema, WarehouseIdSchema]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Omit(DbSchema['wishlist'], [
            'user_id',
            'created_at',
            'updated_at',
            'deleted_at',
          ]),
          Type.Omit(ProductWithPromoResponseSchema, ['wishlist_id', 'prices']),
          Type.Object({
            prices: Type.Array(
              Type.Object({
                price: Type.Number(),
                min_order_quantity: Type.Integer(),
              })
            ),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  deleteWishList: {
    path: '/:id',
    response: Type.Object({ message: Type.String() }),
  },
};
// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export const UserCrudSchema = {
  path: '/user',
  createBuyerUser: {
    path: '/buyer',
    body: Type.Object({
      users: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['users'], ['email', 'last_name', 'first_name']),
          Type.Object({
            shiptos: Type.Array(Type.String({ format: 'uuid' })),
          }),
        ])
      ),
    }),
    response: Type.Object({
      created: Type.Array(MaskedUserSchema),
    }),
  },
  getManyBuyerUser: {
    path: '/buyer',
    query: ReadManyUserBuyerSchema,
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['users'], [
            'id',
            'email',
            'name',
            'first_name',
            'last_name',
            'status',
          ]),
          Type.Object({
            last_login: Type.Integer(),
            icon: Type.String(),
            is_admin: Type.Boolean(),
            req_to_admin: Type.Boolean(),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  getByIdBuyerUser: {
    path: '/buyer/:id',
    params: paramByIDSchema,
    response: Type.Intersect([
      Type.Pick(MaskedUserSchema, [
        'id',
        'email',
        'last_name',
        'first_name',
        'status',
      ]),
      Type.Object({
        request_admin_status: Type.Optional(Type.String()),
        bil_to_number: Type.Optional(Type.String()),
        company_name: Type.Optional(Type.String()),
        shiptos: Type.Array(
          Type.Object({
            id: Type.String({ format: 'uuid' }),
            name: Type.String(),
            is_selected: Type.Optional(Type.Boolean()),
          })
        ),
        role: Type.KeyOf(
          Type.Object({
            admin: Type.String(),
            super_admin: Type.String(),
            cs_katalog: Type.String(),
            cs_cabang: Type.String(),
            collector: Type.String(),
            buyer_bill_to: Type.String(),
            buyer_ship_to: Type.String(),
          })
        ),
      }),
    ]),
  },
  updateBuyerUser: {
    path: '/buyer/:id',
    params: paramByIDSchema,
    body: Type.Object({
      shiptos: Type.Array(
        Type.Object({
          id: Type.String({ format: 'uuid' }),
          name: Type.Optional(Type.String()),
          is_selected: Type.Optional(Type.Boolean()),
        })
      ),
    }),
    response: Type.Intersect([
      Type.Pick(MaskedUserSchema, ['email', 'last_name', 'first_name']),
      Type.Object({
        shiptos: Type.Array(
          Type.Object({
            id: Type.String({ format: 'uuid' }),
            name: Type.String(),
            is_selected: Type.Optional(Type.Boolean()),
          })
        ),
      }),
    ]),
  },
  deleteBuyerUser: {
    path: '/buyer/:id',
    params: paramByIDSchema,
    response: Type.Object({
      message: Type.String(),
    }),
  },
  readManyShipTo: {
    path: '/shipto',
    query: PaginationSchema,
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([Type.Pick(DbSchema['ship_tos'], ['id', 'name'])])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  requestToAdmin: {
    path: '/request/change-to-admin/:id',
    params: paramByIDSchema,
    response: Type.Object({
      no_ticket: Type.Optional(Type.String()),
      message: Type.String(),
    }),
  },
  responseToAdmin: {
    path: '/response/change-to-admin',
    body: Type.Pick(DbSchema['users'], ['email', 'email_status']),
    response: Type.Object({
      message: Type.String(),
    }),
  },
};

// ---------------------------------------------------------------------------
// Bank Account
// ---------------------------------------------------------------------------

export const BankAccountCrudSchema = {
  path: '/bank-account',
  getOneBankAccount: {
    path: '',
    query: Type.Object({
      warehouse: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      bank_account: Type.Union([
        Type.Intersect([
          Type.Omit(DbSchema['bank_accounts'], ['id']),
          Type.Object({
            bank_account_holder: Type.String(),
          }),
        ]),
        Type.Null(),
      ]),
    }),
  },
};

// ---------------------------------------------------------------------------
// Notification
// ---------------------------------------------------------------------------

export const NotificationCrudSchema = {
  path: '/notification',
  readMany: {
    path: '',
    query: PaginationSchema,
    response: Type.Object({
      data: Type.Array(
        Type.Omit(DbSchema['notifications'], [
          'user_id',
          'updated_at',
          'deleted_at',
        ])
      ),
      unread_notification_count: Type.Integer({ minimum: 0 }),
      pagination: PaginationInfoSchema,
    }),
  },
  countUnread: {
    path: '/count',
    response: Type.Object({
      count: Type.Integer({ minimum: 0 }),
    }),
  },
  markAsRead: {
    path: '/:id/read',
    params: paramByIDSchema,
    response: Type.Null(),
  },
  markUnreadAsRead: {
    path: '/read-all-unread',
    response: Type.Null(),
  },
  updatePushNotificationToken: {
    path: '/token',
    body: {
      new_push_notification_token: Type.String(),
    },
    response: Type.Null(),
  },
};

// ---------------------------------------------------------------------------
// Promo
// ---------------------------------------------------------------------------

export const PromoCrudSchema = {
  path: '/promo',
  getManyPromoBuyer: {
    path: '/buyer',
    query: Type.Intersect([
      WarehouseIdSchema,
      PaginationSchema,
      GroupPromoListBuyerSchema,
    ]),
    response: Type.Object({
      data: Type.Array(DbSchema['grouped_promos']),
      pagination: PaginationInfoSchema,
    }),
  },
  getDetailPromo: {
    path: '/buyer/:id/detail',
    params: paramByIDSchema,
    query: WarehouseIdSchema,
    response: Type.Object({
      data: Type.Object({
        promo: Type.Pick(DbSchema['grouped_promos'], [
          'id',
          'mechanism',
          'terms_and_conditions',
          'promo_name',
          'promo_code',
          'promo_type',
          'banner_url',
          'valid_from',
          'valid_until',
        ]),
        products: Type.Array(ProductWithPromoResponseSchema),
      }),
    }),
  },
  getPromoHighlights: {
    path: '/highlights',
    query: WarehouseIdSchema,
    response: Type.Object({
      promo: Type.Optional(DbSchema['grouped_promos']),
      products: Type.Array(ProductResponseSchema),
    }),
  },
  getPromoHighlightsGrouped: {
    path: '/highlights-group',
    query: WarehouseIdSchema,
    response: Type.Object({
      promo: Type.Optional(DbSchema['grouped_promos']),
      products: Type.Array(ProductWithPromoResponseSchema),
    }),
  },
  getCounterGroupPromo: {
    path: '/counter-group',
    query: WarehouseIdSchema,
    response: Type.Object({
      specific_promo_count: Type.Optional(Type.String()),
      general_promo_count: Type.Optional(Type.String()),
    }),
  },
};

//  ---------------------------------------------------------------------------
// Recipe
//  ---------------------------------------------------------------------------
export const MAX_PRODUCTS = 1;

export const RecipeProductResponseSchema = Type.Intersect([
  Type.Pick(Product, [
    'id',
    'item_code',
    'title',
    'minimum_order_quantity',
    'order_quantity_multiple',
    'brand_desc',
    'sales_unit_desc',
  ]),
  Type.Pick(RecipeProduct, ['product_type']),
  Type.Object({
    product_images: Type.Array(Type.String()),
    product_image_thumbnails: Type.Array(Type.String()),
    product_images_medium: Type.Array(Type.String()),
    product_images_large: Type.Array(Type.String()),
    price: Type.Integer(),
    cogs: Type.Integer(),
    cogs_per_portion: Type.Integer(),
    stock: Type.Integer(),
    subtotal: Type.Integer(),
  }),
]);

export const IngredientResponseSchema = Type.Intersect([
  Type.Pick(RecipeIngredient, [
    'id',
    'index',
    'title',
    'amount',
    'amount_unit',
  ]),
  Type.Object({
    recommended_products: Type.Array(RecipeProductResponseSchema, {
      maxItems: MAX_PRODUCTS,
    }),
    alternative_products: Type.Array(RecipeProductResponseSchema, {
      maxItems: MAX_PRODUCTS,
    }),
  }),
]);

export const IngredientGroupResponseSchema = Type.Intersect([
  Type.Pick(RecipeIngredientGroup, ['id', 'index', 'title', 'is_optional']),
  Type.Object({
    ingredients: Type.Array(IngredientResponseSchema),
  }),
]);

export const BuyerRecipeCardResponseSchema = Type.Intersect([
  Type.Pick(Recipe, ['id', 'slug', 'title', 'thumbnail', 'views']),
  Type.Object({
    is_favorite: Type.Boolean(),
    tags: Type.Array(
      Type.Pick(RecipeTag, [
        'name',
        'big_icon_url',
        'small_icon_url',
        'text_color',
        'bg_color',
      ])
    ),
    brands: Type.Array(Type.String()),
  }),
]);

export const BuyerRecipeResponseSchema = Type.Intersect([
  Type.Omit(Recipe, ['created_by', 'updated_by', 'deleted_at', 'deleted_by']),
  Type.Object({
    is_favorite: Type.Boolean(),
    tags: Type.Array(
      Type.Pick(RecipeTag, [
        'id',
        'name',
        'big_icon_url',
        'small_icon_url',
        'text_color',
        'bg_color',
      ])
    ),
    medias: Type.Array(Type.Pick(RecipeMedia, ['url', 'type'])),
    ingredient_groups: Type.Array(IngredientGroupResponseSchema),
    similar_recipes: Type.Array(BuyerRecipeCardResponseSchema),
    recommended_products_total_cogs_per_portion: Type.Integer(),
    alternative_products_total_cogs_per_portion: Type.Integer(),
  }),
]);

export const RecipeCrudSchema = {
  path: '/recipe',
  readManyTagBuyer: {
    path: '/tag',
    query: ReadManyRecipeSchema,
    response: Type.Object({
      data: Type.Array(DbSchema['recipe_tags']),
    }),
  },
  buyerGetRecipeBySlugOrID: {
    path: '/:slugOrID',
    query: Type.Object({
      warehouse: Type.Optional(Type.String({ format: 'uuid' })),
    }),
    params: Type.Object({
      slugOrID: Type.String(),
    }),
    response: BuyerRecipeResponseSchema,
  },
  readCountRecipeByCategory: {
    path: '/count',
    response: Type.Object({
      data: Type.Array(
        Type.Object({
          category: Type.String(),
          count: Type.Number(),
        })
      ),
    }),
  },
  buyerGetRecipeSection: {
    path: '/section',
    response: Type.Array(
      Type.Object({
        title: Type.String(),
        recipes: Type.Array(BuyerRecipeCardResponseSchema),
      })
    ),
  },
  buyerGetRecipeHomepage: {
    path: '/homepage',
    response: Type.Object({
      title: Type.String(),
      recipes: Type.Array(BuyerRecipeCardResponseSchema),
      count: Type.Integer(),
    }),
  },
  toggleRecipeFavorite: {
    path: '/favorite',
    body: Type.Object({
      recipe_id: Type.String(),
    }),
    response: Type.Null(),
  },
  readManyRecipeFavorite: {
    path: '/favorite',
    query: Type.Intersect([
      ReadManySchema,
      Type.Object({
        tags: Type.Optional(Type.String()),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(BuyerRecipeCardResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },
  readManyRecipes: {
    path: '',
    query: Type.Intersect([
      Type.Object({
        search: Type.Optional(Type.String()),
        category: Type.Optional(DbSchema['recipes']['category']),
        tagID: Type.Optional(Type.String()),
        warehouse: Type.Optional(Type.String({ format: 'uuid' })),
        sort_by: Type.Optional(
          Type.Union([
            Type.Literal('view'),
            Type.Literal('created_at'),
            Type.Literal('title_asc'),
            Type.Literal('title_desc'),
          ])
        ),
      }),
      PaginationSchema,
    ]),
    response: Type.Object({
      data: Type.Array(BuyerRecipeCardResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },
  readManyRecipeFavoriteTag: {
    path: '/favorite/tag',
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Pick(DbSchema['recipe_tags'], [
            'id',
            'name',
            'big_icon_url',
            'small_icon_url',
            'text_color',
            'bg_color',
          ]),
          Type.Object({
            count: Type.Number(),
          }),
        ])
      ),
    }),
  },
};

// ---------------------------------------------------------------------------
// Voucher
// ---------------------------------------------------------------------------

// Query schema for voucher product list with search, filter, sort, and pagination
export const VoucherProductListQuerySchema = Type.Intersect([
  Type.Object({
    search_query: Type.Optional(Type.String()),
    size: Type.Number({ minimum: 1, maximum: 100 }),
    page: Type.Number({ minimum: 1 }),
  }),
  Type.Object({
    sort_by: Type.Optional(
      Type.Union([
        Type.Literal('product_name_asc'),
        Type.Literal('product_name_desc'),
        Type.Literal('item_code_asc'),
        Type.Literal('item_code_desc'),
        Type.Literal('brand_name_asc'),
        Type.Literal('brand_name_desc'),
      ])
    ),
    sort_order: Type.Optional(
      Type.Union([Type.Literal('asc'), Type.Literal('desc')])
    ),
  }),
]);

// Response schema for individual product in voucher product list
export const VoucherProductResponseSchema = Type.Intersect([
  Type.Pick(Product, ['id', 'title', 'item_code', 'brand_name', 'brand_desc']),
]);

// Request schema for checking voucher code
export const VoucherCodeCheckRequestSchema = Type.Object({
  voucher_code: Type.String({
    minLength: 1,
    maxLength: VOUCHER_CODE_MAX_LENGTH,
  }),
});

// Response schema for voucher code check
export const VoucherCodeCheckResponseSchema = Type.Object({
  is_valid: Type.Boolean(),
});

// Schema for individual ship-to item in CSV
export const ShipToItemSchema = Type.Object({
  sap_no_shipto: Type.String({ minLength: 1, maxLength: 50 }),
});

// Request schema for CSV upload
export const ShipToCsvUploadRequestSchema = Type.Object({
  file: Type.Object({
    filename: Type.String(),
    mimetype: Type.Literal('text/csv'),
    size: Type.Number({ minimum: 1, maximum: 2097152 }), // 2MB max
  }),
});

// Response schema for CSV upload validation/processing
export const ShipToCsvUploadResponseSchema = Type.Object({
  success: Type.Boolean(),
  total_rows: Type.Number(),
  valid_rows: Type.Number(),
  invalid_rows: Type.Number(),
  url: Type.String(),
  errors: Type.Optional(
    Type.Array(
      Type.Object({
        row: Type.Number(),
        field: Type.Optional(Type.String()),
        message: Type.String(),
      })
    )
  ),
});

// Voucher Creation Configuration Schemas
// ---------------------------------------------------------------------------
export const VoucherTargetConfigurationSchema = Type.Object(
  {
    target_type: Type.Union(
      [Type.Literal('GENERAL'), Type.Literal('SHIP_TO')],
      {
        description: 'Target type for voucher usage',
      }
    ),
    business_segmentations: Type.Array(Type.String(), {
      description: 'Array of business segmentation codes',
    }),
    distribution_channels: Type.Array(Type.String(), {
      description: 'Array of distribution channel codes',
    }),
    business_categories: Type.Array(Type.String(), {
      description: 'Array of business category codes',
    }),
    shipto_csv_url: Nullable(
      Type.String({
        format: 'uri',
        description:
          'CSV file URL containing target ship-to addresses (for SHIP_TO type)',
      })
    ),

    // Usage Limits
    quota: Type.Integer({
      minimum: 1,
      description: 'Total voucher quota available',
    }),
    max_use_per_user: Nullable(
      Type.Integer({
        minimum: 1,
        description: 'Maximum usage per user (null for unlimited)',
      })
    ),
  },
  {
    additionalProperties: false,
    description: 'Target configuration for voucher usage',
  }
);

export const VoucherAcquisitionConfigurationSchema = Type.Object(
  {
    acquisition_type: Type.Union(
      [Type.Literal('REGULAR'), Type.Literal('ACCOUNT_ACTIVATION')],
      {
        description: 'How users can acquire this voucher',
      }
    ),
    valid_day_since_account_signup: Nullable(
      Type.Integer({
        minimum: 1,
        description:
          'Valid days since account signup (for ACCOUNT_ACTIVATION type)',
      })
    ),
  },
  {
    additionalProperties: false,
    description: 'Acquisition configuration for voucher',
  }
);

export const VoucherConditionConfigurationSchema = Type.Object(
  {
    condition_type: Type.Union(
      [Type.Literal('REGULAR'), Type.Literal('MIN_PURCHASE')],
      {
        description: 'Condition type for voucher usage',
      }
    ),
    is_allow_combine_other_voucher: Type.Boolean({
      description: 'Allow combination with other vouchers',
    }),
    is_allow_combine_other_promo: Type.Boolean({
      description: 'Allow combination with other promotions',
    }),
    is_allow_combine_other_special_price: Type.Boolean({
      description: 'Allow combination with special price lists',
    }),
    min_purchase_amount: Nullable(
      Type.Number({
        minimum: 0,
        description:
          'Minimum purchase amount required (for MIN_PURCHASE condition)',
      })
    ),
  },
  {
    additionalProperties: false,
    description: 'Condition configuration for voucher usage',
  }
);

export const VoucherRewardConfigurationSchema = Type.Object(
  {
    reward_type: Type.Union(
      [
        Type.Literal('DISCOUNT_PERCENT'),
        Type.Literal('DISCOUNT_AMOUNT'),
        Type.Literal('FREE_GOODS'),
      ],
      {
        description: 'Type of reward for this voucher',
      }
    ),
    is_bundling: Type.Boolean({
      description: 'Whether this reward requires bundling of products',
    }),
    min_total_quantity_all_item: Nullable(
      Type.Integer({
        minimum: 1,
        description: 'Minimum total quantity for all items (for ALL_ITEM type)',
      })
    ),
    reward_rule_products: Type.Array(
      Type.Object({
        product_item_code: Type.Integer({
          description: 'Product item code',
        }),
        min_quantity: Nullable(
          Type.Integer({
            minimum: 1,
            description: 'Minimum quantity required for this product',
          })
        ),
      }),
      {
        description: 'Array of products with minimum quantity requirements',
      }
    ),
    // Free goods configuration (for FREE_GOODS type)
    free_goods: Type.Optional(
      Type.Array(
        Type.Object({
          product_item_code: Type.Integer({
            description: 'Product item code for free goods',
          }),
          quantity: Type.Integer({
            minimum: 1,
            description: 'Quantity of free goods',
          }),
        }),
        {
          description: 'Array of free goods products and quantities',
        }
      )
    ),
    // Discount products configuration (for DISCOUNT_PRODUCTS type)
    discount_products: Type.Optional(
      Type.Array(
        Type.Object({
          product_item_code: Type.Integer({
            description: 'Product item code for discount',
          }),
          discount_percentage: Nullable(
            Type.Number({
              exclusiveMinimum: 0,
              maximum: 100,
              description: 'Discount percentage for this product',
            })
          ),
          discount_amount: Nullable(
            Type.Number({
              description: 'Discount amount for this product',
            })
          ),
        }),
        {
          description: 'Array of products with specific discount percentages',
        }
      )
    ),
  },
  {
    additionalProperties: false,
    description: 'Reward configuration for voucher',
  }
);

// Extended response schema with product names
export const VoucherGetByIdResponseSchema = Type.Object(
  {
    // Basic Information
    banner_url: Nullable(
      Type.String({
        format: 'uri',
        description: 'URL for voucher banner image',
      })
    ),
    title: Type.String({
      minLength: 1,
      maxLength: 255,
      description: 'Voucher title',
    }),
    code: Type.String({
      minLength: 1,
      maxLength: VOUCHER_CODE_MAX_LENGTH,
      pattern: '^[A-Z0-9_-]+$',
      description:
        'Unique voucher code (uppercase letters, numbers, underscore, hyphen only)',
    }),
    desc: Nullable(
      Type.String({
        maxLength: 1000,
        description: 'Voucher description',
      })
    ),

    // Period Configuration
    period_start_date: Type.String({
      format: 'date',
      description: 'Voucher validity start date (YYYY-MM-DD)',
    }),
    period_end_date: Type.String({
      format: 'date',
      description: 'Voucher validity end date (YYYY-MM-DD)',
    }),

    // Configuration Objects
    target_configuration: VoucherTargetConfigurationSchema,
    acquisition_configuration: VoucherAcquisitionConfigurationSchema,
    condition_configuration: VoucherConditionConfigurationSchema,
    reward_configuration: Type.Object(
      {
        reward_type: Type.Union(
          [
            Type.Literal('DISCOUNT_PERCENT'),
            Type.Literal('DISCOUNT_AMOUNT'),
            Type.Literal('FREE_GOODS'),
          ],
          {
            description: 'Type of reward for this voucher',
          }
        ),
        is_bundling: Type.Boolean({
          description: 'Whether this reward requires bundling of products',
        }),
        min_total_quantity_all_item: Nullable(
          Type.Integer({
            minimum: 1,
            description:
              'Minimum total quantity for all items (for ALL_ITEM type)',
          })
        ),
        reward_rule_products: Type.Array(
          Type.Object({
            product_item_code: Type.Integer({
              description: 'Product item code',
            }),
            product_name: Type.String({
              description: 'Product name',
            }),
            min_quantity: Nullable(
              Type.Integer({
                minimum: 1,
                description:
                  'Minimum quantity required for this product (nullable)',
              })
            ),
          }),
          {
            description:
              'Array of products with minimum quantity requirements and product names',
          }
        ),
        // Free goods configuration with product names (for FREE_GOODS type)
        free_goods: Type.Optional(
          Type.Array(
            Type.Object({
              product_item_code: Type.Integer({
                description: 'Product item code for free goods',
              }),
              product_name: Type.String({
                description: 'Product name for free goods',
              }),
              quantity: Type.Integer({
                minimum: 1,
                description: 'Quantity of free goods',
              }),
            }),
            {
              description:
                'Array of free goods products with names and quantities',
            }
          )
        ),
        // Discount products configuration with product names (for DISCOUNT_PRODUCTS type)
        discount_products: Type.Optional(
          Type.Array(
            Type.Object({
              product_item_code: Type.Integer({
                description: 'Product item code for discount',
              }),
              product_name: Type.String({
                description: 'Product name for discount',
              }),
              discount_percentage: Nullable(
                Type.Number({
                  exclusiveMinimum: 0,
                  maximum: 100,
                  description: 'Discount percentage for this product',
                })
              ),
              discount_amount: Nullable(
                Type.Number({
                  description: 'Discount amount for this product',
                })
              ),
            }),
            {
              description:
                'Array of products with names and specific discount percentages',
            }
          )
        ),
      },
      {
        additionalProperties: false,
        description: 'Reward configuration for voucher with product names',
      }
    ),
  },
  {
    additionalProperties: false,
    description: 'Response schema for getting voucher by ID with product names',
  }
);

// Voucher Get By Code Request/Response Schemas
// ---------------------------------------------------------------------------
export const VoucherGetByCodeRequestSchema = Type.Object(
  {},
  {
    additionalProperties: false,
    description:
      'Request schema for getting voucher details by code (no query parameters)',
  }
);

export const VoucherGetByCodeResponseSchema = Type.Object(
  {
    // Basic Voucher Information
    id: Type.String({
      format: 'uuid',
      description: 'Unique voucher identifier',
    }),
    banner_url: Nullable(
      Type.String({
        format: 'uri',
        description: 'URL for voucher banner image',
      })
    ),
    title: Type.String({
      minLength: 1,
      maxLength: 255,
      description: 'Voucher title',
    }),
    code: Type.String({
      minLength: 1,
      maxLength: VOUCHER_CODE_MAX_LENGTH,
      pattern: '^[A-Z0-9_-]+$',
      description:
        'Unique voucher code (uppercase letters, numbers, underscore, hyphen only)',
    }),
    desc: Nullable(
      Type.String({
        maxLength: 1000,
        description: 'Voucher description',
      })
    ),

    // Period Configuration
    period_start_date: Type.String({
      format: 'date',
      description: 'Voucher validity start date (YYYY-MM-DD)',
    }),
    period_end_date: Type.String({
      format: 'date',
      description: 'Voucher validity end date (YYYY-MM-DD)',
    }),

    is_exceed_quota: Type.Boolean({
      description: 'Whether the voucher exceeds its quota',
    }),

    is_eligible: Type.Boolean({
      description: 'Whether the voucher is eligible for use',
    }),

    // Warning Messages
    warning_messages: Type.Array(
      Type.Object(
        {
          messages: Type.String({
            description: 'Warning message text',
          }),
          products: Type.Array(
            Type.Object(
              {
                product_id: Type.String({
                  format: 'uuid',
                  description: 'Unique product identifier',
                }),
                product_name: Type.String({
                  description: 'Product name',
                }),
                product_item_code: Type.Integer({
                  description: 'Product item code',
                }),
                min_quantity: Type.Integer({
                  minimum: 1,
                  description: 'Minimum quantity required for this product',
                }),
              },
              {
                description:
                  'Product object with id, name, item code, and minimum quantity',
              }
            ),
            {
              description:
                'Array of product objects associated with this warning message',
            }
          ),
        },
        {
          description: 'Warning message object with associated products',
        }
      ),
      {
        description:
          'List of warning message objects with their associated products',
      }
    ),

    // Term Messages
    term_messages: Type.Array(
      Type.Object(
        {
          messages: Type.String({
            description: 'Term message text',
          }),
          products: Type.Array(
            Type.Object(
              {
                product_id: Type.String({
                  format: 'uuid',
                  description: 'Unique product identifier',
                }),
                product_name: Type.String({
                  description: 'Product name',
                }),
                product_item_code: Type.Integer({
                  description: 'Product item code',
                }),
                min_quantity: Type.Integer({
                  minimum: 1,
                  description: 'Minimum quantity required for this product',
                }),
              },
              {
                description:
                  'Product object with id, name, item code, and minimum quantity',
              }
            ),
            {
              description:
                'Array of product objects associated with this term message',
            }
          ),
        },
        {
          description: 'Term message object with associated products',
        }
      ),
      {
        description:
          'List of term message objects with their associated products',
      }
    ),

    // Product Rules with Comprehensive Product Information
    rule_products: Type.Array(ProductWithPromoResponseSchema, {
      description:
        'Array of products with comprehensive information including specifications, pricing, stock, and promotional data',
    }),

    // Free Goods Stock Validation
    free_goods_stock_validation: Type.Optional(
      Type.Object(
        {
          has_insufficient_stock: Type.Boolean({
            description: 'Whether any free goods have insufficient stock',
          }),
          insufficient_stock_items: Type.Array(
            Type.Object(
              {
                product_item_code: Type.Integer({
                  description: 'Product item code with insufficient stock',
                }),
                product_name: Type.String({
                  description: 'Product name',
                }),
                required_quantity: Type.Integer({
                  minimum: 1,
                  description: 'Total required quantity (cart + free goods)',
                }),
                available_stock: Type.Integer({
                  minimum: 0,
                  description: 'Available stock quantity',
                }),
                cart_quantity: Type.Optional(
                  Type.Integer({
                    minimum: 0,
                    description: 'Quantity already in cart',
                  })
                ),
                free_goods_quantity: Type.Optional(
                  Type.Integer({
                    minimum: 1,
                    description: 'Free goods quantity required',
                  })
                ),
              },
              {
                description: 'Product with insufficient stock details',
              }
            ),
            {
              description: 'Array of products with insufficient stock',
            }
          ),
        },
        {
          description: 'Stock validation information for free goods vouchers',
        }
      )
    ),

    // Reward Products
    reward_products: Type.Optional(
      Type.Object(
        {
          free_goods: Type.Optional(
            Type.Array(
              Type.Object(
                {
                  product_item_code: Type.Integer({
                    description: 'Product item code for free goods',
                  }),
                  product_name: Type.String({
                    description: 'Product name',
                  }),
                  quantity: Type.Integer({
                    minimum: 1,
                    description: 'Quantity of free goods',
                  }),
                },
                {
                  description: 'Free goods product with name and quantity',
                }
              ),
              {
                description: 'Array of free goods products',
              }
            )
          ),
          discount_products: Type.Optional(
            Type.Array(
              Type.Object(
                {
                  product_item_code: Type.Integer({
                    description: 'Product item code for discount',
                  }),
                  product_name: Type.String({
                    description: 'Product name',
                  }),
                  discount_percentage: Nullable(
                    Type.Number({
                      exclusiveMinimum: 0,
                      maximum: 100,
                      description: 'Discount percentage for this product',
                    })
                  ),
                  discount_amount: Nullable(
                    Type.Number({
                      description: 'Discount amount for this product',
                    })
                  ),
                },
                {
                  description:
                    'Discount product with name and discount details',
                }
              ),
              {
                description: 'Array of discount products',
              }
            )
          ),
        },
        {
          description:
            'Reward products including free goods and discount products',
        }
      )
    ),

    // Reward Messages
    reward_messages: Type.Optional(
      Type.Array(
        Type.Object(
          {
            messages: Type.String({
              description: 'Reward message text',
            }),
            products: Type.Array(
              Type.Object(
                {
                  product_id: Type.Optional(
                    Type.String({
                      format: 'uuid',
                      description: 'Unique product identifier',
                    })
                  ),
                  product_name: Type.String({
                    description: 'Product name',
                  }),
                  product_item_code: Type.Integer({
                    description: 'Product item code',
                  }),
                  quantity: Type.Optional(
                    Type.Integer({
                      minimum: 1,
                      description: 'Quantity of the product',
                    })
                  ),
                  discount_percent: Type.Optional(
                    Type.Number({
                      exclusiveMinimum: 0,
                      maximum: 100,
                      description: 'Discount percentage for this product',
                    })
                  ),
                  discount_amount: Type.Optional(
                    Type.Number({
                      description: 'Discount amount for this product',
                    })
                  ),
                  savings_amount: Type.Optional(
                    Type.Number({
                      description: 'Savings amount for this product',
                    })
                  ),
                },
                {
                  description: 'Product object with reward details',
                }
              ),
              {
                description:
                  'Array of products associated with this reward message',
              }
            ),
          },
          {
            description: 'Reward message object with associated products',
          }
        ),
        {
          description:
            'List of reward message objects with their associated products',
        }
      )
    ),
  },
  {
    additionalProperties: false,
    description:
      'Response schema for getting voucher details by code with warning messages, terms, and product rules',
  }
);

// Voucher Creation Request/Response Schemas
// ---------------------------------------------------------------------------
export const VoucherCreateRequestSchema = Type.Object(
  {
    // Basic Information
    banner_url: Nullable(
      Type.String({
        format: 'uri',
        description: 'URL for voucher banner image',
      })
    ),
    title: Type.String({
      minLength: 1,
      maxLength: 255,
      description: 'Voucher title',
    }),
    code: Type.String({
      minLength: 1,
      maxLength: VOUCHER_CODE_MAX_LENGTH,
      pattern: '^[A-Z0-9_-]+$',
      description:
        'Unique voucher code (uppercase letters, numbers, underscore, hyphen only)',
    }),
    desc: Nullable(
      Type.String({
        maxLength: 1000,
        description: 'Voucher description',
      })
    ),

    // Period Configuration
    period_start_date: Type.String({
      format: 'date',
      description: 'Voucher validity start date (YYYY-MM-DD)',
    }),
    period_end_date: Type.String({
      format: 'date',
      description: 'Voucher validity end date (YYYY-MM-DD)',
    }),

    // Configuration Objects
    target_configuration: VoucherTargetConfigurationSchema,
    acquisition_configuration: VoucherAcquisitionConfigurationSchema,
    condition_configuration: VoucherConditionConfigurationSchema,
    reward_configuration: VoucherRewardConfigurationSchema,
  },
  {
    additionalProperties: false,
    description: 'Request schema for creating a new voucher',
  }
);

export const VoucherCreateResponseSchema = Type.Object(
  {
    success: Type.Boolean({
      description: 'Indicates if the voucher was created successfully',
    }),
    data: Type.Object({
      voucher_id: Type.String({
        format: 'uuid',
        description: 'Generated UUID for the created voucher',
      }),
      code: Type.String({
        description: 'Voucher code that was created',
        minLength: 1,
        maxLength: VOUCHER_CODE_MAX_LENGTH,
      }),
      title: Type.String({
        description: 'Voucher title',
      }),
    }),
    message: Type.String({
      description: 'Success or error message',
    }),
  },
  {
    additionalProperties: false,
    description: 'Response schema for voucher creation',
  }
);

export const VoucherCreateErrorResponseSchema = Type.Object(
  {
    success: Type.Literal(false),
    error: Type.Object({
      code: Type.String({
        description: 'Error code',
      }),
      message: Type.String({
        description: 'Error message',
      }),
      details: Type.Optional(
        Type.Array(
          Type.Object({
            field: Type.String({
              description: 'Field name that caused the error',
            }),
            message: Type.String({
              description: 'Field-specific error message',
            }),
          })
        )
      ),
    }),
  },
  {
    additionalProperties: false,
    description: 'Error response schema for voucher creation',
  }
);

// Request schema for updating voucher status
export const VoucherUpdateStatusRequestSchema = Type.Object(
  {
    voucher_id: Type.String({
      format: 'uuid',
      description: 'UUID of the voucher to update',
    }),
    status: Type.Union([Type.Literal('ACTIVE'), Type.Literal('INACTIVE')], {
      description: 'New status for the voucher',
    }),
  },
  {
    additionalProperties: false,
    description: 'Request schema for updating voucher status',
  }
);

// Response schema for successful voucher status update
export const VoucherUpdateStatusResponseSchema = Type.Object(
  {
    success: Type.Boolean({
      description: 'Indicates if the voucher status was updated successfully',
    }),
    data: Type.Object({
      voucher_id: Type.String({
        format: 'uuid',
        description: 'UUID of the updated voucher',
      }),
      code: Type.String({
        description: 'Voucher code',
        minLength: 1,
        maxLength: VOUCHER_CODE_MAX_LENGTH,
      }),
      status: Type.Union([Type.Literal('ACTIVE'), Type.Literal('INACTIVE')], {
        description: 'Updated status of the voucher',
      }),
      updated_at: Type.String({
        format: 'date-time',
        description: 'Timestamp when the status was updated',
      }),
    }),
    message: Type.String({
      description: 'Success message',
    }),
  },
  {
    additionalProperties: false,
    description: 'Response schema for successful voucher status update',
  }
);

// Error response schema for voucher status update
export const VoucherUpdateStatusErrorResponseSchema = Type.Object(
  {
    success: Type.Literal(false),
    error: Type.Object({
      code: Type.String({
        description: 'Error code',
      }),
      message: Type.String({
        description: 'Error message',
      }),
      details: Type.Optional(
        Type.Array(
          Type.Object({
            field: Type.String({
              description: 'Field name that caused the error',
            }),
            message: Type.String({
              description: 'Field-specific error message',
            }),
          })
        )
      ),
    }),
  },
  {
    additionalProperties: false,
    description: 'Error response schema for voucher status update',
  }
);

// Voucher Update Schemas
// ---------------------------------------------------------------------------
export const VoucherUpdateRequestSchema = Type.Object(
  {
    banner_url: Nullable(
      Type.String({
        format: 'uri',
        description: 'Updated voucher banner image URL',
      })
    ),
    title: Type.String({
      minLength: 1,
      maxLength: 255,
      description: 'Updated voucher title',
    }),
    desc: Nullable(
      Type.String({
        maxLength: 1000,
        description: 'Updated voucher description',
      })
    ),
    period_start_date: Type.String({
      format: 'date',
      description: 'Updated voucher validity start date (YYYY-MM-DD)',
    }),
    period_end_date: Type.String({
      format: 'date',
      description: 'Updated voucher validity end date (YYYY-MM-DD)',
    }),
    target_configuration: Type.Object(
      {
        quota: Type.Integer({
          minimum: 1,
          description: 'Updated total voucher quota available',
        }),
        max_use_per_user: Nullable(
          Type.Integer({
            minimum: 1,
            description: 'Updated maximum usage per user (null for unlimited)',
          })
        ),
      },
      {
        additionalProperties: false,
        description: 'Target configuration fields allowed during update',
      }
    ),
  },
  {
    additionalProperties: false,
    description: 'Request schema for updating voucher core information',
  }
);

export const VoucherUpdateResponseSchema = Type.Object(
  {
    success: Type.Boolean({
      description: 'Indicates if the voucher update was successful',
    }),
    data: Type.Object(
      {
        voucher_id: Type.String({
          format: 'uuid',
          description: 'UUID of the updated voucher',
        }),
        code: Type.String({
          description: 'Voucher code',
          minLength: 1,
          maxLength: VOUCHER_CODE_MAX_LENGTH,
        }),
        title: Type.String({
          description: 'Updated voucher title',
        }),
        desc: Nullable(
          Type.String({
            description: 'Updated voucher description',
          })
        ),
        banner_url: Nullable(
          Type.String({
            format: 'uri',
            description: 'Updated voucher banner image URL',
          })
        ),
        period_start_date: Type.String({
          format: 'date',
          description: 'Updated voucher validity start date',
        }),
        period_end_date: Type.String({
          format: 'date',
          description: 'Updated voucher validity end date',
        }),
        quota_total: Type.Integer({
          minimum: 0,
          description: 'Total voucher quota set after update',
        }),
        quota_used: Type.Integer({
          minimum: 0,
          description: 'Voucher quota already used',
        }),
        remaining_quota: Type.Integer({
          minimum: 0,
          description: 'Voucher quota remaining after update',
        }),
        max_use_per_user: Nullable(
          Type.Integer({
            minimum: 1,
            description: 'Maximum usage per user after update',
          })
        ),
        updated_at: Type.String({
          format: 'date-time',
          description: 'Timestamp when the voucher was updated',
        }),
      },
      {
        additionalProperties: false,
      }
    ),
    message: Type.String({
      description: 'Success message',
    }),
  },
  {
    additionalProperties: false,
    description: 'Response schema for successful voucher update',
  }
);

export const VoucherUpdateErrorResponseSchema = Type.Object(
  {
    success: Type.Literal(false),
    error: Type.Object(
      {
        code: Type.String({
          description: 'Error code',
        }),
        message: Type.String({
          description: 'Error message',
        }),
        details: Type.Optional(
          Type.Array(
            Type.Object({
              field: Type.String({
                description: 'Field name that caused the error',
              }),
              message: Type.String({
                description: 'Field-specific error message',
              }),
            })
          )
        ),
      },
      {
        additionalProperties: false,
      }
    ),
  },
  {
    additionalProperties: false,
    description: 'Error response schema for voucher update',
  }
);

// Voucher Update Display Schemas
// ---------------------------------------------------------------------------
export const VoucherUpdateDisplayRequestSchema = Type.Object(
  {
    voucher_id: Type.String({
      format: 'uuid',
      description: 'UUID of the voucher to update display setting',
    }),
    is_display: Type.Boolean({
      description:
        'Display setting for the voucher (true to show, false to hide)',
    }),
  },
  {
    additionalProperties: false,
    description: 'Request schema for updating voucher display setting',
  }
);

export const VoucherUpdateDisplayResponseSchema = Type.Object(
  {
    success: Type.Boolean({
      description:
        'Indicates if the voucher display setting was updated successfully',
    }),
    data: Type.Object({
      voucher_id: Type.String({
        format: 'uuid',
        description: 'UUID of the updated voucher',
      }),
      is_display: Type.Boolean({
        description: 'Updated display setting of the voucher',
      }),
      updated_at: Type.String({
        format: 'date-time',
        description: 'Timestamp when the display setting was updated',
      }),
    }),
    message: Type.String({
      description: 'Success message',
    }),
  },
  {
    additionalProperties: false,
    description: 'Response schema for successful voucher display update',
  }
);

export const VoucherUpdateDisplayErrorResponseSchema = Type.Object(
  {
    success: Type.Literal(false),
    error: Type.Object({
      code: Type.String({
        description: 'Error code',
      }),
      message: Type.String({
        description: 'Error message',
      }),
      details: Type.Optional(
        Type.Array(
          Type.Object({
            field: Type.String({
              description: 'Field name that caused the error',
            }),
            message: Type.String({
              description: 'Field-specific error message',
            }),
          })
        )
      ),
    }),
  },
  {
    additionalProperties: false,
    description: 'Error response schema for voucher display update',
  }
);

// Delete Voucher Schemas
export const VoucherDeleteRequestSchema = Type.Object(
  {
    voucher_id: Type.String({
      format: 'uuid',
      description: 'UUID of the voucher to delete',
    }),
  },
  {
    additionalProperties: false,
    description: 'Request schema for deleting a voucher',
  }
);

export const VoucherDeleteResponseSchema = Type.Object(
  {
    success: Type.Boolean({
      description: 'Indicates if the voucher was deleted successfully',
    }),
    data: Type.Object({
      voucher_id: Type.String({
        format: 'uuid',
        description: 'UUID of the deleted voucher',
      }),
      code: Type.String({
        description: 'Code of the deleted voucher',
      }),
      title: Type.String({
        description: 'Title of the deleted voucher',
      }),
      deleted_at: Type.String({
        format: 'date-time',
        description: 'Timestamp when the voucher was deleted',
      }),
    }),
    message: Type.String({
      description: 'Success message',
    }),
  },
  {
    additionalProperties: false,
    description: 'Response schema for successful voucher deletion',
  }
);

export const VoucherDeleteErrorResponseSchema = Type.Object(
  {
    success: Type.Literal(false),
    error: Type.Object({
      code: Type.String({
        description: 'Error code',
      }),
      message: Type.String({
        description: 'Error message',
      }),
      details: Type.Optional(
        Type.Array(
          Type.Object({
            field: Type.String({
              description: 'Field name that caused the error',
            }),
            message: Type.String({
              description: 'Field-specific error message',
            }),
          })
        )
      ),
    }),
  },
  {
    additionalProperties: false,
    description: 'Error response schema for voucher deletion',
  }
);

const UploadBannerSchema = {
  path: '/upload',
  body: Type.Object({
    file: TypeCustom.Custom<SavedMultipartFile>(),
  }),
  response: Type.Object({
    url: Type.String(),
    filename: Type.String(),
  }),
};

// Voucher Eligibility Schemas (Buyer)
// ---------------------------------------------------------------------------
export const VoucherEligibilityItemSchema = Type.Object(
  {
    product_item_code: Type.Integer({
      description: 'SAP product item code present in cart',
      minimum: 1,
    }),
    quantity: Type.Number({
      description: 'Quantity of the item in the cart',
      minimum: 0,
    }),
  },
  { additionalProperties: false }
);

export const VoucherGetByCodeRequestBodySchema = Type.Object(
  {
    warehouse: Type.String({
      description: 'Warehouse identifier',
    }),
    voucher_code: Type.String({
      minLength: 1,
      maxLength: VOUCHER_CODE_MAX_LENGTH,
      description: 'Voucher code to get details for',
    }),
    cart_total_amount: Type.Number({
      description:
        'Cart total amount from GET /cart/total (after promos, before voucher)',
      minimum: 0,
    }),
    items: Type.Array(VoucherEligibilityItemSchema, {
      description: 'Snapshot of cart items to evaluate eligibility against',
    }),
  },
  { additionalProperties: false }
);

export const VoucherEligibilityRequestSchema = Type.Intersect([
  Type.Object(
    {
      warehouse_id: Type.String({
        description: 'Warehouse identifier used for stock validation',
        minLength: 1,
      }),
      cart_total_amount: Type.Number({
        description:
          'Cart total amount from GET /cart/total (after promos, before voucher)',
        minimum: 0,
      }),
      items: Type.Array(VoucherEligibilityItemSchema, {
        description: 'Snapshot of cart items to evaluate eligibility against',
      }),
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      page: Type.Optional(
        Type.Number({ minimum: 1, description: 'Page number for pagination' })
      ),
      size: Type.Optional(
        Type.Number({
          minimum: 1,
          maximum: 100,
          description: 'Number of vouchers per page',
        })
      ),
    },
    { additionalProperties: false }
  ),
]);

export const VoucherEligibilityReasonSchema = Type.Union([
  Type.Object(
    {
      type: Type.Literal('MIN_QUANTITY'),
      product_item_code: Type.Integer({ minimum: 1 }),
      product_id: Type.Optional(
        Type.String({
          format: 'uuid',
          description: 'Unique identifier of the product',
        })
      ),
      product_name: Type.String(),
      required_quantity: Type.Number({ minimum: 0 }),
      current_quantity: Type.Number({ minimum: 0 }),
      brand_name: Type.Optional(Type.String()),
      brand_desc: Type.Optional(Type.String()),
      product_images: Type.Optional(Type.Array(Type.String())),
      product_image_thumbnails: Type.Optional(Type.Array(Type.String())),
      product_images_medium: Type.Optional(Type.Array(Type.String())),
      product_images_large: Type.Optional(Type.Array(Type.String())),
      prices: Type.Optional(
        Type.Array(
          Type.Object(
            {
              price: Type.Number(),
              min_order_quantity: Type.Integer({ minimum: 0 }),
            },
            { additionalProperties: false }
          )
        )
      ),
      stock: Type.Optional(Type.Number({ minimum: 0 })),
      stock_status: Type.Optional(ProductStockStatusSchema),
      pack_unit: Type.Optional(Type.String()),
      sales_unit: Type.Optional(Type.String()),
      minimum_order_quantity: Type.Optional(Type.Integer({ minimum: 0 })),
      order_quantity_multiple: Type.Optional(Type.Integer({ minimum: 0 })),
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal('MIN_TOTAL_QUANTITY'),
      required_quantity: Type.Number({ minimum: 0 }),
      current_quantity: Type.Number({ minimum: 0 }),
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal('MIN_TRANSACTION'),
      required_amount: Type.Number({ minimum: 0 }),
      current_amount: Type.Number({ minimum: 0 }),
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal('CONFLICTING_PROMO'),
      product_item_code: Type.Integer({ minimum: 1 }),
      product_name: Type.String(),
      conflict_source: Type.Union([
        Type.Literal('SAP_PROMO'),
        Type.Literal('FREE_GOOD_PROMO'),
        Type.Literal('SPECIAL_PRICE'),
      ]),
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal('USAGE_LIMIT_REACHED'),
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal('QUOTA_EXHAUSTED'),
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal('NOT_IN_ACTIVATION_WINDOW'),
    },
    { additionalProperties: false }
  ),
]);

export const VoucherEligibilityRewardSchema = Type.Object(
  {
    type: Type.Union([
      Type.Literal('DISCOUNT_PERCENT'),
      Type.Literal('DISCOUNT_AMOUNT'),
      Type.Literal('FREE_GOODS'),
    ]),
    discount_percent: Nullable(
      Type.Number({ exclusiveMinimum: 0, maximum: 100 })
    ),
    discount_amount: Nullable(Type.Number({ minimum: 0 })),
    free_goods: Type.Optional(
      Type.Array(
        Type.Object(
          {
            product_item_code: Type.Integer({ minimum: 1 }),
            product_name: Type.String(),
            quantity: Type.Number({ minimum: 0 }),
          },
          { additionalProperties: false }
        )
      )
    ),
    discount_products: Type.Optional(
      Type.Array(
        Type.Object(
          {
            product_item_code: Type.Integer({ minimum: 1 }),
            product_name: Type.String(),
            discount_percent: Nullable(
              Type.Number({ exclusiveMinimum: 0, maximum: 100 })
            ),
            discount_amount: Nullable(Type.Number({ minimum: 0 })),
          },
          { additionalProperties: false }
        )
      )
    ),
    reward_rule_products: Type.Array(
      Type.Object(
        {
          product_item_code: Type.Integer({ minimum: 1 }),
          product_name: Type.String(),
          min_quantity: Nullable(Type.Number({ minimum: 0 })),
        },
        { additionalProperties: false }
      )
    ),
    is_bundling: Type.Boolean(),
    min_total_quantity_all_item: Nullable(Type.Number({ minimum: 0 })),
  },
  { additionalProperties: false }
);

export const VoucherEligibilityResponseItemSchema = Type.Object(
  {
    id: Type.String({ format: 'uuid' }),
    code: Type.String(),
    title: Type.String(),
    description: Nullable(Type.String()),
    banner_url: Nullable(Type.String()),
    period_start: Type.Integer({ minimum: 0 }),
    period_end: Type.Integer({ minimum: 0 }),
    allow_other_vouchers: Type.Boolean(),
    division: Type.Union([
      Type.Literal('diamond'),
      Type.Literal('trading'),
      Type.Literal('fnb'),
      Type.Literal('equipment'),
      Type.Null(),
    ]),
    reward: VoucherEligibilityRewardSchema,
    usage: Type.Object(
      {
        remaining_global: Nullable(Type.Number({ minimum: 0 })),
        remaining_per_customer: Nullable(Type.Number({ minimum: 0 })),
      },
      { additionalProperties: false }
    ),
    is_eligible: Type.Boolean(),
    ineligibility_reasons: Type.Optional(
      Type.Array(VoucherEligibilityReasonSchema)
    ),
    message_rule_products: Type.Optional(
      Type.Array(
        Type.Object(
          {
            message: Type.String(),
            products: Type.Optional(
              Type.Array(
                Type.Object(
                  {
                    product_item_code: Type.Integer({ minimum: 1 }),
                    product_name: Type.String(),
                    min_quantity: Nullable(Type.Number({ minimum: 0 })),
                  },
                  { additionalProperties: false }
                )
              )
            ),
          },
          { additionalProperties: false }
        )
      )
    ),
    message_term_vouchers: Type.Optional(
      Type.Array(
        Type.Object(
          {
            message: Type.String(),
          },
          { additionalProperties: false }
        )
      )
    ),
  },
  { additionalProperties: false }
);

export const VoucherEligibilityResponseSchema = Type.Object(
  {
    data: Type.Array(VoucherEligibilityResponseItemSchema),
    pagination: Type.Object(
      {
        page: Type.Number({ minimum: 1 }),
        size: Type.Number({ minimum: 1 }),
        rows: Type.Number({ minimum: 0 }),
        pages: Type.Number({ minimum: 0 }),
      },
      { additionalProperties: false }
    ),
  },
  { additionalProperties: false }
);

export const VoucherValidateRequestSchema = Type.Object(
  {
    vouchers: Type.Array(
      Type.Object(
        {
          id: Type.String({ format: 'uuid' }),
        },
        { additionalProperties: false }
      )
    ),
    user_id: Type.String({ format: 'uuid' }),
    warehouse_id: Type.String({ format: 'uuid' }),
    order_meta_id: Type.String({ format: 'uuid' }),
  },
  { additionalProperties: false }
);

export const VoucherValidateResponseItemSchema = Type.Object(
  {
    voucher_id: Type.String({ format: 'uuid' }),
    is_valid: Type.Boolean(),
  },
  { additionalProperties: false }
);

export const VoucherValidateResponseSchema = Type.Object(
  {
    data: Type.Array(VoucherValidateResponseItemSchema),
  },
  { additionalProperties: false }
);

export const VoucherCrudSchema = {
  path: '/voucher',

  list: {
    path: '',
    query: Type.Intersect([
      Type.Pick(SearchSchema, ['search_query']),
      PaginationSchema,
      Type.Object({
        start_date: Type.Optional(Type.Integer({ minimum: 0 })),
        end_date: Type.Optional(Type.Integer({ minimum: 0 })),
        status: Type.Optional(
          Type.Union([
            Type.Literal('ACTIVE'),
            Type.Literal('INACTIVE'),
            Type.Literal('active'),
            Type.Literal('inactive'),
          ])
        ),
        is_display: Type.Optional(Type.Boolean()),
        mechanism: Type.Optional(
          Type.Array(
            Type.Union([
              Type.Literal('DISCOUNT_PERCENT'),
              Type.Literal('DISCOUNT_AMOUNT'),
              Type.Literal('FREE_GOODS'),
              Type.Literal('discount_percent'),
              Type.Literal('discount_amount'),
              Type.Literal('free_goods'),
            ]),
            { minItems: 1 }
          )
        ),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Object({
          id: Type.String({ format: 'uuid' }),
          code: Type.String(),
          title: Type.String(),
          desc: Nullable(Type.String()),
          banner_url: Nullable(Type.String({ format: 'uri' })),
          period_start: Type.Integer({ minimum: 0 }),
          period_end: Type.Integer({ minimum: 0 }),
          status: Type.Union([
            Type.Literal('ACTIVE'),
            Type.Literal('INACTIVE'),
            Type.Literal('active'),
            Type.Literal('inactive'),
          ]),
          is_display: Type.Boolean(),
          display_order: Type.Number(),
          reward_configuration: Type.Object({
            reward_type: Type.Union([
              Type.Literal('DISCOUNT_PERCENT'),
              Type.Literal('DISCOUNT_AMOUNT'),
              Type.Literal('FREE_GOODS'),
              Type.Literal('discount_percent'),
              Type.Literal('discount_amount'),
              Type.Literal('free_goods'),
            ]),
            is_bundling: Nullable(
              Type.Boolean({
                description:
                  'Whether this reward requires bundling of products',
              })
            ),
            min_total_quantity_all_item: Nullable(
              Type.Integer({
                minimum: 1,
                description: 'Minimum total quantity for all items',
              })
            ),
            discount_percentage: Nullable(
              Type.Number({
                exclusiveMinimum: 0,
                maximum: 100,
                description:
                  'Discount percentage applied when reward type is DISCOUNT_PERCENT',
              })
            ),
            discount_amount: Nullable(
              Type.Number({
                minimum: 0,
                description:
                  'Discount amount applied when reward type is DISCOUNT_AMOUNT',
              })
            ),
            free_goods: Nullable(
              Type.Array(
                Type.Object({
                  product_item_code: Type.Integer({
                    description: 'Product item code for free goods',
                  }),
                  quantity: Type.Integer({
                    minimum: 1,
                    description: 'Quantity of free goods',
                  }),
                })
              )
            ),
            reward_rule_products: Type.Array(
              Type.Object({
                product_item_code: Type.Integer({
                  description: 'Product item code',
                }),
                min_quantity: Nullable(
                  Type.Integer({
                    minimum: 1,
                    description: 'Minimum quantity required for this product',
                  })
                ),
              })
            ),
          }),
          target_configuration: Type.Object({
            quota_total: Type.Integer({ minimum: 0 }),
            quota_used: Type.Integer({ minimum: 0 }),
            max_use_per_user: Nullable(Type.Integer({ minimum: 1 })),
            distribution_channels: Type.Object({
              count: Type.Integer({ minimum: 0 }),
              is_all: Type.Boolean(),
              items: Type.Array(Type.String()),
            }),
            cg1_business_segmentations: Type.Array(Type.String()),
            cg2_business_categories: Type.Array(Type.String()),
          }),
          created_at: Type.Integer({ minimum: 0 }),
          updated_at: Type.Integer({ minimum: 0 }),
        })
      ),
      pagination: PaginationInfoSchema,
    }),
  },

  create: {
    path: '/create',
    body: VoucherCreateRequestSchema,
    response: Type.Union([
      VoucherCreateResponseSchema,
      VoucherCreateErrorResponseSchema,
    ]),
  },

  getProductList: {
    path: '/products',
    query: VoucherProductListQuerySchema,
    response: Type.Object({
      data: Type.Array(VoucherProductResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },

  checkCode: {
    path: '/check-code',
    body: VoucherCodeCheckRequestSchema,
    response: VoucherCodeCheckResponseSchema,
  },

  validate: {
    path: '/validate',
    body: VoucherValidateRequestSchema,
    response: VoucherValidateResponseSchema,
  },

  getByCode: {
    path: '/get-by-code',
    query: VoucherGetByCodeRequestSchema,
    body: VoucherGetByCodeRequestBodySchema,
    response: VoucherGetByCodeResponseSchema,
  },

  uploadShipToCsv: {
    path: '/upload-shipto-csv',
    body: ShipToCsvUploadRequestSchema,
    response: ShipToCsvUploadResponseSchema,
  },
  uploadBanner: UploadBannerSchema,
  updateStatus: {
    path: '/update-status',
    body: VoucherUpdateStatusRequestSchema,
    response: Type.Union([
      VoucherUpdateStatusResponseSchema,
      VoucherUpdateStatusErrorResponseSchema,
    ]),
  },
  update: {
    path: '/:id',
    params: paramByIDSchema,
    body: VoucherUpdateRequestSchema,
    response: Type.Union([
      VoucherUpdateResponseSchema,
      VoucherUpdateErrorResponseSchema,
    ]),
  },

  updateDisplay: {
    path: '/update-display',
    body: VoucherUpdateDisplayRequestSchema,
    response: Type.Union([
      VoucherUpdateDisplayResponseSchema,
      VoucherUpdateDisplayErrorResponseSchema,
    ]),
  },
  reorderVoucher: {
    path: '/:id/reorder',
    params: paramByIDSchema,
    body: Type.Object({
      index: Type.Number({ minimum: 0 }),
    }),
    response: Type.Null(),
  },
  bringToTopVoucher: {
    path: '/:id/bring-to-top',
    params: paramByIDSchema,
    response: Type.Null(),
  },
  delete: {
    path: '/delete',
    body: VoucherDeleteRequestSchema,
    response: Type.Union([
      VoucherDeleteResponseSchema,
      VoucherDeleteErrorResponseSchema,
    ]),
  },

  getEligibility: {
    path: '/eligibility',
    body: VoucherEligibilityRequestSchema,
    response: VoucherEligibilityResponseSchema,
  },

  getById: {
    path: '/:id',
    params: paramByIDSchema,
    response: VoucherGetByIdResponseSchema,
  },
};

export type VoucherCrud = RecursiveStatic<typeof VoucherCrudSchema>;

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------
type EndpointSchema = Record<string, TSchema>;
type PluginSchema = Record<string, EndpointSchema>;
export const ApiSchemas: Record<string, PluginSchema> = {
  auth: removeKey(AuthSchema),
  account: removeKey(AccountCrudSchema),
  banner: removeKey(BannerCrudSchema),
  cart: removeKey(CartCrudSchema),
  order: removeKey(OrderCrudSchema),
  product: removeKey(ProductCrudSchema),
  wishlist: removeKey(WishlistCrudSchema),
  user: removeKey(UserCrudSchema),
  bank: removeKey(BankAccountCrudSchema),
  notification: removeKey(NotificationCrudSchema),
  promo: removeKey(PromoCrudSchema),
  recipe: removeKey(RecipeCrudSchema),
  voucher: removeKey(VoucherCrudSchema),
};
